// References (docs/app.md → References). One job: maintain the master records
// that drive matching — vendors, companies (with their uploader
// email domains), the chart and its two halves, and the fleet.
// Gated to the References role; a fleet maintainer sees the Vehicles manager
// alone. The API enforces access; this is UX.
//
// Five managers, not six: the provider-key roster belonged to the claims
// workspace, which is out of scope, and is deleted with it.
//
// Three rules run through every manager here:
//   * Every list is a TABLE — real column headers, aligned columns, Status
//     last, Edit the only row action (owner items 17–22, 2026-08-28).
//   * Retire and reactivate are ONE write — a PATCH of `is_active` carrying the
//     row's version. There are no separate deactivate/reactivate verbs, and
//     since 2026-08-28 no Retire BUTTON either: `is_active` is a Status field
//     inside each editor, saved with everything else. Vehicles is the one
//     exception — it has the fleet director's own `status` and no `is_active`
//     control at all (`docs/questions.md`).
//   * Uniqueness is a database constraint, not a pre-flight check. The old
//     screen asked "does this already exist?" before every create, which both
//     cost a round trip and raced; now a violation comes back as
//     `duplicate_key` and its message is shown as written.
import { FLEET_COMPANY_NAME, VEHICLE_STATUS, VENDOR_IMPORT_MAX_BYTES, type VehicleStatus } from '@ap/shared'

/** What a new vehicle's Status opens on — the server's own create default. */
const NEW_VEHICLE_STATUS: VehicleStatus = VEHICLE_STATUS.InService
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react'
import {
  Badge,
  Body1,
  Button,
  Caption1,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Divider,
  Dropdown,
  Field,
  Option,
  Radio,
  RadioGroup,
  Spinner,
  Subtitle2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { FieldInput, FieldCombobox } from '../components/FieldControls'
import { AppDialog } from '../components/AppDialog'
import { WarningBanner } from '../components/WarningBanner'
import { errantZipSentence } from '../lib/postalCode'
import {
  AddRegular,
  ArrowImportRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
} from '@fluentui/react-icons'
import { errorMessage, writeError } from '../api/errors'
import { useToast } from '../components/useToast'
import {
  FLEET_FIELDS,
  FLEET_FIELD_LABEL,
  type FleetField,
  VENDOR_IMPORT_FIELDS,
  VENDOR_IMPORT_FIELD_LABEL,
  type ReferenceSection,
  VEHICLE_STATUSES,
} from '../api/types'
import { REFERENCE_SECTION_LABEL, REFERENCE_SECTIONS } from '../lib/constants'
import { categoryNoun, countOf, NOTHING_CHANGED, savedToast, sentenceCase, sentenceList, withArticle } from '../lib/format'
import type {
  Code,
  Company,
  CompanyEmailDomain,
  Department,
  Account,
  MarketingCategory,
  TransportationCategory,
  Vehicle,
  Vendor,
  InactiveCascade,
} from '../lib/types'
import { SCREEN_SUBHEADER } from '../lib/constants'
import {
  commitVehicleImport,
  commitVendorImport,
  createCode,
  createCompany,
  createDepartment,
  createAccount,
  createMarketingCategory,
  createTransportationCategory,
  createVehicle,
  createVendor,
  loadAllCodes,
  loadAllDepartments,
  loadAllAccounts,
  loadCompaniesForManagement,
  loadCompanyEmailDomains,
  loadDepartments,
  loadAccounts,
  loadMarketingCategoriesForManagement,
  loadTransportationCategoriesForManagement,
  loadVehiclesForManagement,
  loadVendorsForManagement,
  loadErrantZipVendors,
  previewVehicleImport,
  previewVendorImport,
  updateCode,
  updateCompany,
  updateDepartment,
  updateAccount,
  previewInactiveCascade,
  updateMarketingCategory,
  updateTransportationCategory,
  updateVehicle,
  setVendorActive,
  updateVendor,
  nothingChanged,
} from '../lib/data'
import {
  composeCodeId,
  decomposeCodeId,
  halfMatchesSearch,
  type HalfOption,
  maskDepartmentId,
  maskAccountId,
  soleDepartmentForAccount,
  type MaskedId,
} from '../lib/codeId'
import { StatusToggle } from '../components/StatusToggle'
import { PageTabs } from '../components/PageTabs'
import { ScreenIntro } from '../components/ScreenIntro'
import { ErrorBanner } from '../components/States'
import { ImportChanges, ImportCounts, ImportDialog, ImportHint, ImportNames, ImportNotice, SkippedRows } from '../components/ImportDialog'
import { useImportFlow } from '../lib/importFlow'
import { popupPositionProps, usePopupListboxClass } from '../components/popupPositioning'
import { useDialogFormStyles } from '../components/dialogForm'
import { SortableHeaderCell } from '../components/SortableHeaderCell'
import { useRoomyTableClass, useClickableRowClass, rowActivationProps } from '../components/tableDensity'
import { ListFilters } from '../components/ListFilters'
import { FrozenWhileClosed } from '../components/FrozenWhileClosed'
import { KeptView } from '../components/KeptView'
import { ScreenActions } from '../components/ScreenActions'
import { BulkBar, BulkCheckbox, BulkSelectAllCheckbox } from '../components/BulkActions'
import { useBulkCellClass, useBulkMark, type RowBulk } from '../lib/bulkSelection'
import { StatusDot } from '../components/StatusDot'
import { SegmentedControl } from '../components/SegmentedControl'
import { sortRows, useTableSort } from '../lib/tableSort'
import { DEFAULT_STATUS_FILTER, matchesStatus, type StatusFilter } from '../lib/statusFilter'
import { activeVendorsBefore, importWouldGutTheList } from '../lib/vendorImportGuard'
import { useDialogError } from '../lib/hooks'
import { MakeInactiveDialog, type InactiveListSection } from '../components/MakeInactiveDialog'
import {
  cascadeCounts,
  cascadeIsEmpty,
  cascadeHalfNames,
  codingRuleListItem,
  nameWithId,
  UNNAMED,
  combinedKnockOns,
  mergeCascades,
  inactiveToast,
  CODE_REACTIVATION_NOTE,
  IN_PROGRESS_NOTE,
  makeInactiveLabel,
  reactivationNote,
} from '../lib/inactiveCascade'

const useStyles = makeStyles({
  // The Vendor dialog's two address groups (D-241): a rule, then the heading.
  dialogSectionRule: { marginTop: 0, marginBottom: 0 },
  // An `h3` (D2-02): the margin is set in full so the browser's own heading
  // margin does not move it.
  dialogSectionHeading: { display: 'block', margin: 0 },
  // Fluent's `DialogContent` ships `overflow-y: auto` with a max-height, so a
  // three-field dialog still rendered a scrollbar (owner items 21 and 22,
  // 2026-08-28: "The pop-up also is currently scrollable; it should not be").
  // Both have to go — `overflow: visible` alone still clips at the max-height.
  dialogNoScroll: { overflowY: 'visible', maxHeight: 'none' },
  // The picker + its clear button, on the Add default dialog's two halves.
  pickerRow: { display: 'flex', gap: '8px', alignItems: 'flex-end' },
  // The two outline badges on a Senders row (intake allowed/not, routes-to/none).
  senderBadges: { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  // Inline, non-blocking dialog warning (mirrors States.tsx warningBanner).
  // `display: 'block'` is load-bearing, not decoration: Fluent's `<Text>` root
  // (what `Body1` renders) is a `<span>` with `display: inline`, and a
  // multi-line inline box paints its background/border/padding PER LINE BOX
  // (box-decoration-break: slice) rather than around the whole block — the
  // mangled, strikethrough-looking warning the owner screenshotted. Setting it
  // here, on the class every `dialogWarn` caller shares, means no caller has to
  // remember the `block` prop (one of ten call sites here used to; the other
  // nine didn't, which is why only one rendered correctly).
  dialogWarn: {
    display: 'block',
    color: tokens.colorPaletteYellowForeground2,
    backgroundColor: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '8px 12px',
  },
  // `display: 'block'` for the same reason `dialogWarn` needs it: `Body1`
  // renders a `<span>` (`display: inline` by default), so `marginBottom` was a
  // no-op and multiple consecutive `mdIntro` paragraphs ran together with no
  // gap or even a word-break between them — found on the new three-paragraph
  // Senders intro, which is the only place this class had ever been used more
  // than once in a row.
  mdIntro: { display: 'block', color: tokens.colorNeutralForeground3, marginBottom: '12px' },
  mdHint: { color: tokens.colorNeutralForeground3, padding: '8px 2px' },
  mdList: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    maxHeight: '52vh',
    overflowY: 'auto',
  },
  mdRowActions: { display: 'flex', gap: '4px', alignItems: 'center' },
  mdInactive: { opacity: 0.55 },
  // `display: block` SPELLED OUT (QA wave B1, 2026-09-22, B1-03). Fluent's
  // `Body1` is a `Text`, whose own class sets the element inline whatever tag
  // `as` renders — so `as="p"` alone left the two sentences running together
  // ("…reactivate it later.1 coding rule…") and dropped the margin with them,
  // which is the defect 2026-09-21's B1-08 was meant to close.
  confirmParagraph: { display: 'block' },
  // Two columns: nine fields in one stack is a scroll, and these pair naturally
  // (number/year, VIN/plate, make/model, company/department).
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '8px 2px' },
  // The two import dialogs' own drop zone is gone — both use the Upload
  // screen's, via `components/FileDropZone.tsx` (owner items 17 and 20,
  // 2026-08-28). Its styling lives there.
  // ---- Codes (owner item 9, 2026-08-19) ----
  // Every master table sits in this: a wide table scrolls inside its own box
  // rather than pushing the page sideways. Per-table min-widths are passed as
  // `minWidth` to `MasterTable`, since the five differ.
  codesScroll: { overflowX: 'auto', maxWidth: '100%', minWidth: 0 },
  // The Add Reference dialog's two inline fields for a NEW half (ID, Name),
  // beside each other rather than stacked, same idea as `vehGrid`.
  // The standard's two-column row (`grid2`), with the ID narrower than the
  // Name and each field top-aligned, so an ID's error line moves nothing.
  halfNewRow: { gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)', alignItems: 'start' },
  // Mirrors `EntityPicker.tsx`'s own option styling — duplicated rather than
  // imported, since `HalfPicker` is a freeform combobox and cannot reuse
  // `EntityPicker` itself (app/README.md; E3).
  optionPrimary: { color: tokens.colorNeutralForeground1 },
  optionSecondary: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginLeft: '8px' },
})

/** `MasterTable` renders the Status column itself rather than taking it in
    `columns`, so its label is a constant both the header and the sort key use.
    No other column may be called this. */
const STATUS_COLUMN = 'Status'

export function TrainingScreen({
  fleetOnly = false,
  sections,
}: {
  fleetOnly?: boolean
  /** The References tabs this user holds a role for, in tab order (owner item
      29). Omitted only by tests and by callers that mean "all of them". */
  sections?: readonly ReferenceSection[]
}) {
  return (
    <div>
      {!fleetOnly && <ScreenIntro>{SCREEN_SUBHEADER.References}</ScreenIntro>}
      <MasterData fleetOnly={fleetOnly} sections={sections} />
    </div>
  )
}

/** A term that settles before it is used for a server search. */
function useDebounced(value: string, ms = 300): string {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return settled
}

/** A master list as a REAL TABLE — column headers, aligned cells, one row per
    record (owner items 17–22, 2026-08-28: "I want the table to look more like
    a table"). It replaced the stacked title/subtitle `MasterRow` list on every
    tab.

    There is no Retire button here, deliberately: retire and reactivate moved
    into each editor's own Status field, so a status change is a deliberate
    edit-and-save rather than a one-click action on a row.

    `Status` is the last column on every table and is rendered by
    `statusCell` — an Active / Inactive badge off `is_active` by default, but
    Vehicles passes its own, because the fleet's `status` is the director's
    vocabulary and is what a fleet manager reads (owner item 20: "'Active'
    is not one of the Status options in the editor. The label should reflect
    the actual value"). On that tab `is_active` is now DERIVED from `status`
    (D-65), so the two columns would have said the same thing twice.

    EVERY COLUMN HEADER SORTS, STATUS INCLUDED (owner, 2026-09-01: "Status
    should sort. I need to be able to see Active vs Inactive"). A column carries
    its own `sort` reader — the plain text behind the cell, which is not always
    what the cell renders — and clicking its header orders the rows by it,
    ascending first, descending on a second click.

    Status sorts on the WORD IN THE CELL, so it groups the way the reader sees
    it: Active before Inactive by default, and on Vehicles the fleet director's
    own In Service / Out of Service / Sold. A caller passing `statusCell` must
    pass `statusSort` with it, or the column would sort by a badge it is not
    showing. Only Edit does not sort — it is a button.

    Sorting reorders the rows this table was handed, so on Vendors it reorders
    the current page rather than paging the whole master. */
function MasterTable<T>({
  ariaLabel,
  columns,
  rows,
  cells,
  rowLabel,
  inactive,
  isBusy,
  onEdit,
  statusCell,
  statusSort,
  minWidth,
  bulk,
}: {
  ariaLabel: string
  /** The columns BEFORE Status — label, an optional fixed pixel width, and the
      text the header sorts on. A column with no `sort` does not sort. */
  columns: { label: string; width?: number; sort?: (row: T) => string }[]
  rows: readonly T[]
  /** One cell per entry in `columns`, in the same order. */
  cells: (row: T) => ReactElement[] | (string | ReactElement)[]
  /** Names one row for a screen reader, now that the ROW is the edit control
      (owner, 2026-09-11) — "Edit 1 LIFE FIRE PROTECTION" rather than the bare
      "Edit" every pencil used to announce, which said nothing about which of
      two hundred rows it would open. */
  rowLabel: (row: T) => string
  inactive: (row: T) => boolean
  /** Is this row mid-write? A PREDICATE rather than a key, so the table's one
      remaining key (`bulk.keyOf`) is the selection's and nothing else's. */
  isBusy?: (row: T) => boolean
  onEdit: (row: T) => void
  /** Overrides the Active / Inactive badge — Vehicles shows its own status. */
  statusCell?: (row: T) => ReactElement | string
  /** The text the Status column sorts on. Required alongside `statusCell`,
      which may render something this component cannot read back. */
  statusSort?: (row: T) => string
  minWidth?: number
  /** Bulk selection AND the function that names a row (owner, 2026-09-14;
      reshaped for QA wave B1, B1-01). The same `keyOf` keys the selection and
      the row's checkbox, because they were two props and they disagreed —
      `lib/bulkSelection.ts`'s header has the whole story. It is also React's
      list key here, so a list has exactly one. */
  bulk: RowBulk<T>
}) {
  const s = useStyles()
  // The SAME row density Rules uses (owner, 2026-09-11) — one shared class, not
  // a second copy of the number: `app/src/components/tableDensity.ts`.
  const roomy = useRoomyTableClass()
  const clickable = useClickableRowClass()
  const bulkCell = useBulkCellClass()
  const sort = useTableSort<string>()
  // Sorted by column LABEL, which is unique within a table and is already this
  // component's own key for a header cell. Not memoised: every caller builds
  // `columns` as a fresh array literal on each render, so a memo keyed on it
  // would recompute every render anyway and only add a stale-closure hazard.
  // Status is not in `columns` — this component renders it itself — so it gets
  // its reader here, and a caller's `statusSort` wins over the default badge
  // text whenever the caller is also overriding the cell.
  const readStatus = statusSort ?? ((row: T) => (inactive(row) ? 'Inactive' : 'Active'))
  const sortedColumn = columns.find((c) => c.label === sort.state?.key)
  const readSorted =
    sort.state?.key === STATUS_COLUMN ? readStatus : sortedColumn?.sort
  const ordered =
    sort.state && readSorted ? sortRows(rows, sort.state, { [sort.state.key]: readSorted }) : rows
  return (
    <div className={s.codesScroll}>
      <Table
        size="small"
        aria-label={ariaLabel}
        className={roomy}
        style={minWidth ? { minWidth } : undefined}
      >
        <TableHeader>
          <TableRow>
            {/* SELECT ALL, UNLABELLED, IN THE CORNER (owner, 2026-09-15: "put
                the Select All checkbox in the top-left corner within the table,
                unlabeled, and drop 'Select all'"). It was a labelled checkbox
                on a strip above the table, and that strip is what stood between
                the sub-tabs and the first row on every visit. Here it sits
                directly above the column of row checkboxes it controls. */}
            <TableHeaderCell className={bulkCell}>
              <BulkSelectAllCheckbox selection={bulk.selection} label={ariaLabel.toLowerCase()} />
            </TableHeaderCell>
            {columns.map((c) =>
              c.sort ? (
                <SortableHeaderCell
                  key={c.label}
                  style={c.width ? { width: c.width } : undefined}
                  sortDirection={sort.directionFor(c.label)}
                  onSort={() => sort.toggle(c.label)}
                >
                  {c.label}
                </SortableHeaderCell>
              ) : (
                <TableHeaderCell key={c.label} style={c.width ? { width: c.width } : undefined}>
                  {c.label}
                </TableHeaderCell>
              ),
            )}
            <SortableHeaderCell
              style={{ width: 110 }}
              sortDirection={sort.directionFor(STATUS_COLUMN)}
              onSort={() => sort.toggle(STATUS_COLUMN)}
            >
              {STATUS_COLUMN}
            </SortableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ordered.map((row) => {
            const key = bulk.keyOf(row)
            const isInactive = inactive(row)
            const busy = isBusy?.(row) ?? false
            return (
              // THE ROW OPENS THE EDITOR (owner, 2026-09-11). The Edit column
              // and its per-row pencil are gone; `rowActivationProps` carries
              // over the keyboard path that button provided. A row mid-write is
              // inert and says so with the spinner that used to sit beside the
              // pencil — activating it again would open an editor over a record
              // whose stored state is still moving.
              <TableRow
                key={key}
                className={mergeClasses(
                  !busy && clickable,
                  isInactive ? s.mdInactive : undefined,
                )}
                {...(busy ? {} : rowActivationProps(() => onEdit(row), `Edit ${rowLabel(row)}`))}
              >
                <TableCell className={bulkCell}>
                  <BulkCheckbox selection={bulk.selection} rowKey={key} label={rowLabel(row)} />
                </TableCell>
                {cells(row).map((cell, i) => (
                  <TableCell key={columns[i]?.label ?? i}>{cell}</TableCell>
                ))}
                <TableCell>
                  <div className={s.mdRowActions}>
                    {statusCell ? statusCell(row) : <StatusDot active={!isInactive} />}
                    {busy && <Spinner size="tiny" />}
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}


// 'defaults' (vendor coding rules) and 'senders' (intake/routing) moved to the
// Rules screen verbatim (owner item 10a, 2026-08-13) — see RulesScreen.tsx.
// 'accounts' renamed to 'codes' (owner item 9, 2026-08-19). THE VALUES BELOW
// ARE FROZEN — only the LABELS have moved: on 2026-08-28 (owner items 19, 21,
// 22) Codes → "Accounts & Departments", Marketing → "Marketing Categories",
// Transportation → "Transportation Categories"; and on 2026-09-01 the first of
// those went BACK to "Codes", because "Code" now names one specific thing — an
// Account paired with a Department — rather than being loose shorthand for the
// tab. Renaming a tab value buys nothing and breaks anything holding one.
// The RULES screen keeps "Accounts & Departments" on its own tab: what it
// maintains is vendor coding rules, not codes.
// 'marketing' / 'transportation' / 'vehicles' (owner item 11, 2026-08-19) are
// SECTIONS, each its own grant, gated on `references` like every other tab here
// (never `rules`; they are staff-authored masters, not routing data).
//
// THE TABS ARE NAMED FOR WHERE THE DATA IS USED ON AN INVOICE LINE (owner,
// 2026-09-28 — D-193): *"I want Other Requirements and Associated Entities tabs.
// Other Requirements should offer the Marketing Category and Transportation
// Category tables, while Associated Entities should offer the Vehicles table."*
// The two line icons carry exactly those names, so a reader who meets a value
// in a pop-up finds its list under the tab of the same name. This replaces the
// department-per-tab shape of 2026-09-15 (Marketing; Transportation with
// Vehicles and Categories).
//
// A tab holding more than one table offers a switcher at the far end of the tab
// row, like Codes, and only when the reader holds more than one of them.
type EntityTab = 'vendors' | 'companies' | 'codes' | 'additional-detail' | 'associated-entities'

/** The two masters behind the Additional Detail tab, left to right and in the
    order the arrow keys walk them. Each is its own grant: a reader is offered
    only the ones they hold, and a reader who holds one gets that list with no
    switcher at all. */
type AdditionalDetailView = 'marketing' | 'transportation'

const ADDITIONAL_DETAIL_VIEWS: readonly { value: AdditionalDetailView; label: string }[] = [
  { value: 'marketing', label: 'Marketing Categories' },
  { value: 'transportation', label: 'Transportation Categories' },
]

const ADDITIONAL_DETAIL_TAB_LABEL = 'Additional Detail'
const ASSOCIATED_ENTITIES_TAB_LABEL = 'Associated Entities'

/** The Associated Entities tab's one table, as a ONE-OPTION switcher (owner,
    2026-09-29: "please add a Vehicles slider selector like we have for
    Marketing Categories and Transportation Categories, except it would be one
    of one options"). It names the table the tab is showing, in the same place
    and the same control as the Additional Detail tab's, so Staff and
    Participant lists can join it later as options rather than as a new
    control. */
type AssociatedEntitiesView = 'vehicles'

const ASSOCIATED_ENTITIES_VIEWS: readonly { value: AssociatedEntitiesView; label: string }[] = [
  { value: 'vehicles', label: 'Vehicles' },
]

// Section value -> this screen's tab value. The Codes section and its tab are
// both `codes`; the tab value has not changed since owner item 9 (renaming a tab
// value buys nothing and breaks anything holding one — see the EntityTab
// comment above).
const TAB_FOR_SECTION: Readonly<Record<ReferenceSection, EntityTab>> = {
  vendors: 'vendors',
  companies: 'companies',
  codes: 'codes',
  // D-193: the three line-level masters sit under the tab named for the line
  // icon that uses them.
  vehicles: 'associated-entities',
  marketing: 'additional-detail',
  transportation: 'additional-detail',
}

function MasterData({
  fleetOnly = false,
  sections = REFERENCE_SECTIONS,
}: {
  fleetOnly?: boolean
  sections?: readonly ReferenceSection[]
}) {
  // Owner item 29, 2026-08-28: a user sees only the tabs they hold a role for.
  // `sections` arrives from `visibleReferenceTabs(session)` in App.tsx — the
  // server's own answer (`capabilities.reference_sections`), with no fallback
  // since the coarse `references` role went (D-293). A HINT, NEVER
  // THE BOUNDARY: every write route re-asks `canManageReferenceSection`, so a
  // hidden tab and a refused write are two independent facts.
  const shown = useMemo(
    () => sections.map((section) => TAB_FOR_SECTION[section]),
    [sections],
  )
  // Seed the selection from what is actually offered, so a holder of one tab
  // does not land on a `vendors` tab that is not rendered.
  const [tab, setTab] = useState<EntityTab>(
    fleetOnly ? 'associated-entities' : (shown[0] ?? 'vendors'),
  )
  // Which of the Codes tab's three masters is showing. Lifted out of
  // `CodesManager` on 2026-09-14 because the switcher renders in the tab
  // row now, above the manager rather than inside it.
  const [codesView, setCodesView] = useState<CodesView>('combined')
  // WHICH OF THE ADDITIONAL DETAIL TAB'S TWO MASTERS IS SHOWING (D-193). Same
  // control and same place as the Codes tab's — the far end of the tab row.
  const [additionalDetailView, setAdditionalDetailView] = useState<AdditionalDetailView>('marketing')
  /** The Additional Detail views this reader actually holds, in fixed order.
      Empty means the tab does not render at all. */
  const additionalDetailTabs = useMemo(
    () => ADDITIONAL_DETAIL_VIEWS.filter((v) => sections.includes(v.value)),
    [sections],
  )
  // Land on one they hold, for the same reason `tab` does below.
  useEffect(() => {
    if (additionalDetailTabs.length > 0 && !additionalDetailTabs.some((v) => v.value === additionalDetailView)) {
      setAdditionalDetailView(additionalDetailTabs[0]!.value)
    }
  }, [additionalDetailTabs, additionalDetailView])
  // The grant can change under a live tab (a role added or removed, a `/me`
  // refresh). Fall back to the first tab still offered rather than rendering
  // an empty body under a selection that no longer exists.
  useEffect(() => {
    if (!fleetOnly && shown.length > 0 && !shown.includes(tab)) setTab(shown[0])
  }, [fleetOnly, shown, tab])
  const [associatedEntitiesView, setAssociatedEntitiesView] = useState<AssociatedEntitiesView>('vehicles')
  const associatedEntitiesSwitch = (
    <SegmentedControl
      ariaLabel="Which entity table to show"
      value={associatedEntitiesView}
      options={ASSOCIATED_ENTITIES_VIEWS}
      onChange={setAssociatedEntitiesView}
    />
  )
  // A fleet maintainer has authority over vehicles and nothing else, so the
  // header offers the one tab that holds them — Associated Entities, the tab
  // every other reader finds the fleet under — and nothing more.
  if (fleetOnly) {
    return (
      <div>
        <PageTabs
          tabs={[{ value: 'associated-entities', label: ASSOCIATED_ENTITIES_TAB_LABEL }]}
          selectedValue="associated-entities"
          onSelect={() => {}}
          ariaLabel="Master data tables"
          trailing={associatedEntitiesSwitch}
        />
        <ScreenIntro>
          Inactive is reversible — an Inactive vehicle drops out of tagging and automatic resolution but stays for audit.
        </ScreenIntro>
        <VehiclesManager />
      </div>
    )
  }
  // Which tabs render at all is `shown`, above. Additional Detail holds two
  // masters and offers whichever this reader is granted; Associated Entities
  // holds the fleet (D-193).
  const headerTabs: { value: EntityTab; label: string; show: boolean }[] = [
    { value: 'vendors', label: REFERENCE_SECTION_LABEL.vendors, show: shown.includes('vendors') },
    { value: 'companies', label: REFERENCE_SECTION_LABEL.companies, show: shown.includes('companies') },
    { value: 'codes', label: REFERENCE_SECTION_LABEL.codes, show: shown.includes('codes') },
    { value: 'additional-detail', label: ADDITIONAL_DETAIL_TAB_LABEL, show: additionalDetailTabs.length > 0 },
    { value: 'associated-entities', label: ASSOCIATED_ENTITIES_TAB_LABEL, show: shown.includes('associated-entities') },
  ]
  return (
    <div>
      {/* THE TABS RIDE THE HEADER, and the view switcher rides the same row
          beside them (owner, 2026-09-14: "I am not in love with a slider under
          a tab under a tab"; 2026-09-28 moved the row itself into the header,
          `components/PageTabs.tsx`). The switcher is still not a filter and
          still a segmented control — Accounts, Departments and Codes are three
          different RECORDS. Same move on Rules → Companies. Additional
          Detail shows its switcher only to a reader who holds both
          tables; Associated Entities always shows its one-option Vehicles
          switch (owner, 2026-09-29). */}
      <PageTabs
        tabs={headerTabs.filter((t) => t.show)}
        selectedValue={tab}
        onSelect={setTab}
        ariaLabel="Master data tables"
        trailing={
          tab === 'codes' && shown.includes('codes') ? (
            <SegmentedControl
              ariaLabel="Which records to show"
              value={codesView}
              options={CODES_VIEWS}
              onChange={setCodesView}
            />
          ) : tab === 'additional-detail' && additionalDetailTabs.length > 1 ? (
            <SegmentedControl
              ariaLabel="Which category table to show"
              value={additionalDetailView}
              options={additionalDetailTabs}
              onChange={setAdditionalDetailView}
            />
          ) : tab === 'associated-entities' && sections.includes('vehicles') ? (
            associatedEntitiesSwitch
          ) : undefined
        }
      />
      {tab === 'vendors' && shown.includes('vendors') && <VendorsManager />}
      {tab === 'companies' && shown.includes('companies') && <CompaniesManager />}
      {tab === 'codes' && shown.includes('codes') && <CodesManager view={codesView} />}
      {/* The per-view guard reads the SECTION list, not `shown` — `shown` holds
          tab values, and both category tables share the Additional Detail tab.
          Same third-explicit-check pattern every gated screen here carries.
          BOTH VIEWS MOUNT WITH THE TAB and the one not chosen is hidden
          (`components/KeptView.tsx`), so a switch is one paint, as on Codes,
          not a reload behind a spinner (owner, 2026-10-07). */}
      {tab === 'additional-detail' && sections.includes('marketing') && (
        <KeptView active={additionalDetailView === 'marketing'}>
          <MarketingCategoriesManager />
        </KeptView>
      )}
      {tab === 'additional-detail' && sections.includes('transportation') && (
        <KeptView active={additionalDetailView === 'transportation'}>
          <TransportationCategoriesManager />
        </KeptView>
      )}
      {tab === 'associated-entities' && sections.includes('vehicles') && <VehiclesManager />}
    </div>
  )
}

// ---- Vendors ---------------------------------------------------------------
/** The ZIP Code filter's one narrowing value (D-241). */
const ERRANT_ZIP = 'errant'
/** Rows painted per page. The master is ~2,700 rows. */
const VENDOR_PAGE = 50
/** How many the server hands back per search. Deep enough that the unfiltered
    list is useful immediately, shallow enough not to ship the whole master. */
const VENDOR_FETCH = 200

// The list is never blank: an empty search loads the first page of the master
// and paints it. Typing searches the WHOLE master server-side (name, code or
// city) rather than filtering the rows already on screen — with 2,700 vendors,
// local filtering would quietly miss most matches.
function VendorsManager() {
  const s = useStyles()
  const toast = useToast()
  const [term, setTerm] = useState('')
  const settledTerm = useDebounced(term)
  const [status, setStatus] = useState<StatusFilter>(DEFAULT_STATUS_FILTER)
  const [rows, setRows] = useState<Vendor[] | null>(null)
  const [page, setPage] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [edit, setEdit] = useState<Vendor | 'new' | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  // ERRANT ZIP CODES (owner, 2026-09-30, D-241). `zipFilter` is the ZIP Code
  // filter ('' is any ZIP); `errant` is the warning's own read, the ACTIVE
  // vendors with an errant ZIP across the whole master, asked of the server
  // because this list holds only one search's worth of it.
  const [zipFilter, setZipFilter] = useState<'' | typeof ERRANT_ZIP>('')
  const [errant, setErrant] = useState<{ vendors: Vendor[]; total: number } | null>(null)

  const load = useCallback(async () => {
    setError(null)
    void loadErrantZipVendors()
      .then(setErrant)
      .catch(() => setErrant(null))
    try {
      setRows(
        await loadVendorsForManagement(settledTerm.trim() || undefined, VENDOR_FETCH, {
          errantZip: zipFilter === ERRANT_ZIP,
        }),
      )
    } catch (e) {
      setError(errorMessage(e))
      setRows([])
    }
  }, [settledTerm, zipFilter])
  useEffect(() => {
    void load()
  }, [load])
  /** The warning's button: exactly the vendors it counts — Active, any text, errant ZIP. */
  const showErrant = () => {
    setStatus(DEFAULT_STATUS_FILTER)
    setTerm('')
    setZipFilter(ERRANT_ZIP)
  }
  const errantShown =
    zipFilter === ERRANT_ZIP && status.length === 1 && status[0] === 'active' && term === ''

  const matched = useMemo(() => (rows ?? []).filter((v) => matchesStatus(status, v.is_active)), [rows, status])

  // A filter change must not leave you stranded on a page that no longer exists.
  useEffect(() => {
    setPage(0)
  }, [settledTerm, status, zipFilter])

  const pageCount = Math.max(1, Math.ceil(matched.length / VENDOR_PAGE))
  const current = Math.min(page, pageCount - 1)
  const shown = matched.slice(current * VENDOR_PAGE, current * VENDOR_PAGE + VENDOR_PAGE)

  // BULK MARK ACTIVE / INACTIVE (owner, 2026-09-14). Over `shown` — the fifty
  // rows the pager is showing — never `matched`, and never the whole 6,210-row
  // master. It was `matched` until QA wave B1 (B1-02): at `1–50 of 161` the
  // strip read `Mark 161 Inactive`, which is a write over 111 records the
  // reader could not see. This is the one References list that pages, so it is
  // the one where the two differ; `lib/bulkSelection.ts`'s header has the rule.
  const bulk = useBulkMark({
    rows: shown,
    keyOf: (v: Vendor) => v.id,
    isActive: (v: Vendor) => v.is_active !== false,
    write: (v: Vendor, active: boolean) => setVendorActive(v.vendor_id, active, v.row_version),
    noun: 'vendor',
    onDone: (m) => {
      toast(m)
      void load()
    },
    onError: setError,
  })

  if (rows === null) return <Spinner label="Loading vendors…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      {/* The page's own tools live in the header's top-right cluster since
          2026-09-14 (owner) - see `components/ScreenActions.tsx`. */}
      <ScreenActions>
        {/* A DIFFERENT GLYPH FROM UPLOAD (owner, 2026-09-14: "I do not want it
            to be confused with our Upload button/pop-up"). Both controls used
            `ArrowUploadRegular`, and on a queue page they would now sit in the
            same cluster - two identical arrows meaning two very different
            things: Upload sends one document into the pipeline, Import
            replaces a whole master list from a spreadsheet. `ArrowImport` is
            an arrow into a tray rather than an arrow off a line. */}
        <Tooltip content="Import" relationship="label">
          <Button appearance="secondary" icon={<ArrowImportRegular />} onClick={() => setImportOpen(true)} />
        </Tooltip>
        <Tooltip content="Add Reference" relationship="label">
          <Button appearance="secondary" icon={<AddRegular />} onClick={() => setEdit('new')} />
        </Tooltip>
        <ListFilters
          status={status}
          onStatusChange={setStatus}
          term={term}
          onTermChange={setTerm}
          searchPlaceholder="Search name, ID, or city…"
          ariaLabel="Vendors"
          selects={[
            {
              label: 'ZIP Code',
              value: zipFilter,
              onChange: (v) => setZipFilter(v === ERRANT_ZIP ? ERRANT_ZIP : ''),
              options: [{ value: ERRANT_ZIP, label: 'Errant' }],
            },
          ]}
        />
      </ScreenActions>
      {errant !== null && errant.total > 0 && (
        <WarningBanner
          action={
            errantShown ? undefined : (
              <Button appearance="secondary" onClick={showErrant}>
                Show These Vendors
              </Button>
            )
          }
        >
          {errantZipSentence(errant.vendors, errant.total)}
        </WarningBanner>
      )}
      {matched.length === 0 ? (
        <Body1 className={s.mdHint}>No vendors match.</Body1>
      ) : (
        <>
          <BulkBar selection={bulk.selection} busy={bulk.busy} onMark={bulk.mark} />
          <MasterTable
            bulk={bulk}
            ariaLabel="Vendors"
            columns={[
              { label: 'ID', width: 140, sort: (v) => v.vendor_id },
              { label: 'Name', sort: (v) => v.vendor_name || '' },
            ]}
            rows={shown}
            cells={(v) => [v.vendor_id, v.vendor_name || '(unnamed)']}
            rowLabel={(v) => v.vendor_name || v.vendor_id}
            inactive={(v) => !v.is_active}
            onEdit={setEdit}
            minWidth={520}
          />
          <div className={s.pager}>
            <Button
              size="small"
              appearance="subtle"
              icon={<ChevronLeftRegular />}
              disabled={current === 0}
              onClick={() => setPage(current - 1)}
            >
              Previous
            </Button>
            <Caption1 className={s.mdHint}>
              {current * VENDOR_PAGE + 1}–{current * VENDOR_PAGE + shown.length} of {matched.length}
              {matched.length >= VENDOR_FETCH ? ' matched so far — narrow the search for more' : ''}
            </Caption1>
            <Button
              size="small"
              appearance="subtle"
              icon={<ChevronRightRegular />}
              iconPosition="after"
              disabled={current >= pageCount - 1}
              onClick={() => setPage(current + 1)}
            >
              Next
            </Button>
          </div>
        </>
      )}
      <VendorDialog
        row={edit}
        onClose={() => setEdit(null)}
        onSaved={(message) => {
          setEdit(null)
          toast(message)
          void load()
        }}
      />
      <VendorImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onApplied={(summary) => {
          setImportOpen(false)
          toast(`The vendor list was imported: ${summary}.`)
          void load()
        }}
      />
    </div>
  )
}

/** The editable shape behind VendorDialog — the vendor master's contact
    columns, which the matching pass uses as disambiguation hints. */
interface VendorFields {
  vendorId: string
  vendorName: string
  address1: string
  address2: string
  address3: string
  city: string
  state: string
  zip: string
  phone: string
  fax: string
  // The Remittance Address (D-241).
  remittanceAddress1: string
  remittanceAddress2: string
  remittanceAddress3: string
  remittanceCity: string
  remittanceState: string
  remittanceZip: string
}
// `useDialogError` — the hook every editor below holds its own refusal in —
// moved to `lib/hooks.ts` on 2026-09-22 (QA wave A, A-01), when the Rules
// screen was found to have the same defect References fixed on 2026-09-21 and
// needed the same hook. Its reasoning moved with it.
const BLANK_VENDOR: VendorFields = {
  vendorId: '', vendorName: '', address1: '', address2: '', address3: '',
  city: '', state: '', zip: '', phone: '', fax: '',
  remittanceAddress1: '', remittanceAddress2: '', remittanceAddress3: '',
  remittanceCity: '', remittanceState: '', remittanceZip: '',
}

function VendorDialog({
  row,
  onClose,
  onSaved,
}: {
  row: Vendor | 'new' | null
  onClose: () => void
  /** The toast, from `savedToast` — the dialog knows whether Status moved. */
  onSaved: (message: string) => void
}) {
  const s = useStyles()
  const df = useDialogFormStyles()
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  const [f, setF] = useState<VendorFields>(BLANK_VENDOR)
  // The two address sections' headings, which name their groups (D2-02).
  const primaryHeadingId = useId()
  const remittanceHeadingId = useId()
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useDialogError(row)

  // Seed on open (references only `row`, so no exhaustive-deps escape needed).
  useEffect(() => {
    if (!row) return
    if (row === 'new') {
      setF(BLANK_VENDOR)
      setIsActive(true)
      return
    }
    setIsActive(row.is_active)
    setF({
      vendorId: row.vendor_id ?? '',
      vendorName: row.vendor_name ?? '',
      address1: row.address_1 ?? '',
      address2: row.address_2 ?? '',
      address3: row.address_3 ?? '',
      city: row.city ?? '',
      state: row.state_or_province ?? '',
      zip: row.postal_code ?? '',
      phone: row.phone ?? '',
      fax: row.fax ?? '',
      remittanceAddress1: row.remittance_address_1 ?? '',
      remittanceAddress2: row.remittance_address_2 ?? '',
      remittanceAddress3: row.remittance_address_3 ?? '',
      remittanceCity: row.remittance_city ?? '',
      remittanceState: row.remittance_state_or_province ?? '',
      remittanceZip: row.remittance_postal_code ?? '',
    })
  }, [row])

  const set = (k: keyof VendorFields) => (_: unknown, d: { value: string }) => setF((p) => ({ ...p, [k]: d.value }))
  const ready = f.vendorId.trim() !== '' && f.vendorName.trim() !== ''

  async function save() {
    if (!ready) return
    setSaving(true)
    try {
      if (isNew) {
        await createVendor({
          vendor_id: f.vendorId.trim(),
          vendor_name: f.vendorName.trim(),
          address_1: f.address1.trim() || null,
          address_2: f.address2.trim() || null,
          address_3: f.address3.trim() || null,
          city: f.city.trim() || null,
          state_or_province: f.state.trim() || null,
          postal_code: f.zip.trim() || null,
          phone: f.phone.trim() || null,
          fax: f.fax.trim() || null,
          remittance_address_1: f.remittanceAddress1.trim() || null,
          remittance_address_2: f.remittanceAddress2.trim() || null,
          remittance_address_3: f.remittanceAddress3.trim() || null,
          remittance_city: f.remittanceCity.trim() || null,
          remittance_state_or_province: f.remittanceState.trim() || null,
          remittance_postal_code: f.remittanceZip.trim() || null,
        })
      } else if (existing) {
        const next = {
          vendor_name: f.vendorName.trim(),
          // Status is part of the same save now — there is no Retire button on
          // the row any more (owner item 17, 2026-08-28).
          is_active: isActive,
          // Edit sends the current text for every contact field (blank clears it).
          address_1: f.address1.trim() || null,
          address_2: f.address2.trim() || null,
          address_3: f.address3.trim() || null,
          city: f.city.trim() || null,
          state_or_province: f.state.trim() || null,
          postal_code: f.zip.trim() || null,
          phone: f.phone.trim() || null,
          fax: f.fax.trim() || null,
          remittance_address_1: f.remittanceAddress1.trim() || null,
          remittance_address_2: f.remittanceAddress2.trim() || null,
          remittance_address_3: f.remittanceAddress3.trim() || null,
          remittance_city: f.remittanceCity.trim() || null,
          remittance_state_or_province: f.remittanceState.trim() || null,
          remittance_postal_code: f.remittanceZip.trim() || null,
        }
        // A Save that changed nothing writes nothing (D-170).
        if (nothingChanged(existing, next)) {
          onSaved(NOTHING_CHANGED)
          return
        }
        await updateVendor(existing.vendor_id, { row_version: existing.row_version, ...next })
      }
      onSaved(savedToast('vendor', { wasNew: isNew, activeBefore: existing?.is_active, activeAfter: isActive }))
    } catch (e) {
      setSaveError(writeError(e, 'Could not save vendor'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppDialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <FrozenWhileClosed open={!!row}><DialogBody>
          <DialogTitle>{isNew ? 'Add Reference' : 'Edit Reference'}</DialogTitle>
          <DialogContent className={mergeClasses(df.stack, df.beforeActions)}>
            <ErrorBanner message={saveError} />
            <Field label="Vendor ID" required>
              <FieldInput value={f.vendorId} onChange={set('vendorId')} readOnly={!isNew} appearance={isNew ? undefined : 'filled-darker'} />
            </Field>
            <Field label="Vendor Name" required>
              <FieldInput value={f.vendorName} onChange={set('vendorName')} />
            </Field>
            {/* Two address groups, each opened by a rule and a heading, with
                the same field labels in both (owner, 2026-09-30, D-241).
                EACH GROUP IS NAMED BY ITS HEADING (QA 2026-10-01, D2-02). The
                labels repeat on purpose, so without the group a screen reader
                read two identical sets of fields, and the primary City could
                not be told from the remittance City. The title is an `h3`
                under the dialog's own `h2`, and the group's name. */}
            <Divider className={s.dialogSectionRule} />
            <div role="group" aria-labelledby={primaryHeadingId} className={df.stack}>
              <Subtitle2 as="h3" id={primaryHeadingId} className={s.dialogSectionHeading}>Primary Address</Subtitle2>
              <Field label="Address Line 1">
                <FieldInput value={f.address1} onChange={set('address1')} />
              </Field>
              <Field label="Address Line 2">
                <FieldInput value={f.address2} onChange={set('address2')} />
              </Field>
              <Field label="Address Line 3">
                <FieldInput value={f.address3} onChange={set('address3')} />
              </Field>
              <Field label="City">
                <FieldInput value={f.city} onChange={set('city')} />
              </Field>
              <Field label="State / Province">
                <FieldInput value={f.state} onChange={set('state')} />
              </Field>
              <Field label="ZIP / Postal Code">
                <FieldInput value={f.zip} onChange={set('zip')} />
              </Field>
              <Field label="Phone">
                <FieldInput value={f.phone} onChange={set('phone')} />
              </Field>
              <Field label="Fax">
                <FieldInput value={f.fax} onChange={set('fax')} />
              </Field>
            </div>
            <Divider className={s.dialogSectionRule} />
            <div role="group" aria-labelledby={remittanceHeadingId} className={df.stack}>
              <Subtitle2 as="h3" id={remittanceHeadingId} className={s.dialogSectionHeading}>Remittance Address</Subtitle2>
              <Field label="Address Line 1">
                <FieldInput value={f.remittanceAddress1} onChange={set('remittanceAddress1')} />
              </Field>
              <Field label="Address Line 2">
                <FieldInput value={f.remittanceAddress2} onChange={set('remittanceAddress2')} />
              </Field>
              <Field label="Address Line 3">
                <FieldInput value={f.remittanceAddress3} onChange={set('remittanceAddress3')} />
              </Field>
              <Field label="City">
                <FieldInput value={f.remittanceCity} onChange={set('remittanceCity')} />
              </Field>
              <Field label="State / Province">
                <FieldInput value={f.remittanceState} onChange={set('remittanceState')} />
              </Field>
              <Field label="ZIP / Postal Code">
                <FieldInput value={f.remittanceZip} onChange={set('remittanceZip')} />
              </Field>
            </div>
          </DialogContent>
          {!isNew && (
            <DialogActions position="start">
              <StatusToggle value={isActive} onChange={setIsActive} disabled={saving} />
            </DialogActions>
          )}
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              Save
            </Button>
          </DialogActions>
        </DialogBody></FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}

// ---- Companies -------------------------------------------------------------
// RENAMING IS ALLOWED (a deliberate change, not an inherited restriction). In
// the old system record ownership was resolved by team NAME, so a rename handed
// every newly released record to the catch-all team and blinded the company's
// own director. Authority and visibility key on the company ID here, so the
// hazard is gone.
//
// UPLOADER EMAIL DOMAINS ARE NOT EDITED HERE (owner, 2026-09-29, D-206: "This
// portion is decided in Rules"). Rules → Companies → Domains owns them. This
// list still reads them, for its search box only.
function CompaniesManager() {
  const s = useStyles()
  const toast = useToast()
  const [all, setAll] = useState<Company[] | null>(null)
  const [domains, setDomains] = useState<CompanyEmailDomain[]>([])
  const [status, setStatus] = useState<StatusFilter>(DEFAULT_STATUS_FILTER)
  const [term, setTerm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [dialogRow, setDialogRow] = useState<Company | 'new' | null>(null)

  // TWO READS, AND ONLY ONE OF THEM IS THIS SCREEN'S (QA re-verification,
  // 2026-09-22, N-03). The company list is gated on References → Companies; the
  // uploader domains beside it are gated on RULES → Companies, because that is
  // the tab that owns them. A holder of the References token alone is therefore
  // refused the second read — and under `Promise.all` that refusal took the
  // whole screen down: a red banner and `No companies to show.`, on a tab the
  // reader is entitled to. The domains are a search term and a line of display
  // text here, nothing more, so a refused read leaves them empty and says
  // nothing. A refused COMPANY read is still the screen's own failure and still
  // reports.
  const load = useCallback(async () => {
    setError(null)
    const domainsRead = loadCompanyEmailDomains().then(
      (ds) => ds,
      () => [] as CompanyEmailDomain[],
    )
    try {
      const [cs, ds] = await Promise.all([loadCompaniesForManagement(), domainsRead])
      setAll(cs)
      setDomains(ds)
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  /** A company's active domains, by company id. */
  const domainsFor = useCallback(
    (companyId: string) => domains.filter((d) => d.is_active && d.company_id === companyId),
    [domains],
  )

  const visible = useMemo(() => {
    const t = term.trim().toLowerCase()
    return (all ?? [])
      .filter((c) => matchesStatus(status, c.is_active))
      .filter(
        (c) =>
          !t ||
          c.company_name.toLowerCase().includes(t) ||
          c.company_id.toLowerCase().includes(t) ||
          domainsFor(c.company_id).some((d) => d.domain.toLowerCase().includes(t)),
      )
  }, [all, status, term, domainsFor])

  const bulk = useBulkMark({
    rows: visible,
    keyOf: (c: Company) => c.id,
    isActive: (c: Company) => c.is_active !== false,
    write: (c: Company, active: boolean) =>
      updateCompany(c.company_id, { is_active: active, row_version: c.row_version }),
    noun: 'company',
    onDone: (m) => {
      toast(m)
      void load()
    },
    onError: setError,
  })

  if (all === null) return <Spinner label="Loading companies…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      {/* The page's own tools live in the header's top-right cluster since
          2026-09-14 (owner) - see `components/ScreenActions.tsx`. */}
      <ScreenActions>
        <Tooltip content="Add Reference" relationship="label">
          <Button appearance="secondary" icon={<AddRegular />} onClick={() => setDialogRow('new')} />
        </Tooltip>
        <ListFilters
          status={status}
          onStatusChange={setStatus}
          term={term}
          onTermChange={setTerm}
          searchPlaceholder="Search name, ID, or email domain…"
          ariaLabel="Companies"
        />
      </ScreenActions>
      {visible.length === 0 ? (
        <Body1 className={s.mdHint}>{(all ?? []).length === 0 ? 'No companies to show.' : 'No companies match.'}</Body1>
      ) : (
        <>
        <BulkBar selection={bulk.selection} busy={bulk.busy} onMark={bulk.mark} />
        <MasterTable
          bulk={bulk}
          ariaLabel="Companies"
          columns={[
            { label: 'ID', width: 140, sort: (c) => c.company_id },
            { label: 'Company', sort: (c) => c.company_name || '' },
          ]}
          rows={visible}
          cells={(c) => [c.company_id, c.company_name || '(unnamed)']}
          rowLabel={(c) => c.company_name || c.company_id}
          inactive={(c) => !c.is_active}
          onEdit={setDialogRow}
          minWidth={520}
        />
        </>
      )}
      <CompanyDialog
        row={dialogRow}
        onClose={() => setDialogRow(null)}
        onSaved={(msg) => {
          setDialogRow(null)
          toast(msg)
          void load()
        }}
      />
    </div>
  )
}

// Two fields and the status, since D-206. The domains section went to Rules,
// which owns it; the yellow note about renames went because the owner asked
// for it gone. Labels are Title Case, and the ID locks on edit exactly as the
// Vendors pop-up's does.
function CompanyDialog({
  row,
  onClose,
  onSaved,
}: {
  row: Company | 'new' | null
  onClose: () => void
  onSaved: (message: string) => void
}) {
  const df = useDialogFormStyles()
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  const [companyId, setCompanyId] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useDialogError(row)

  useEffect(() => {
    if (!row) return
    if (row === 'new') {
      setCompanyId('')
      setCompanyName('')
      setIsActive(true)
      return
    }
    setCompanyId(row.company_id)
    setCompanyName(row.company_name)
    setIsActive(row.is_active)
  }, [row])

  const ready = companyId.trim() !== '' && companyName.trim() !== ''
  const renamed = !!existing && companyName.trim() !== existing.company_name
  const statusChanged = !!existing && isActive !== existing.is_active

  async function save() {
    if (!ready) return
    setSaving(true)
    try {
      if (isNew) {
        await createCompany({ company_id: companyId.trim(), company_name: companyName.trim() })
        onSaved('The company was added.')
      } else if (existing) {
        // Name and status ride in ONE patch — two writes would burn the row
        // version on the first and 409 the second.
        if (renamed || statusChanged) {
          await updateCompany(existing.company_id, {
            ...(renamed ? { company_name: companyName.trim() } : {}),
            ...(statusChanged ? { is_active: isActive } : {}),
            row_version: existing.row_version,
          })
          onSaved(
            savedToast('company', {
              activeBefore: statusChanged ? !isActive : undefined,
              activeAfter: statusChanged ? isActive : undefined,
            }),
          )
        } else {
          onSaved(NOTHING_CHANGED)
        }
      }
    } catch (e) {
      setSaveError(writeError(e, isNew ? 'Could not add company' : 'Could not save company'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppDialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <FrozenWhileClosed open={!!row}><DialogBody>
          <DialogTitle>{isNew ? 'Add Reference' : 'Edit Reference'}</DialogTitle>
          <DialogContent className={mergeClasses(df.stack, df.beforeActions)}>
            <ErrorBanner message={saveError} />
            <Field label="Company ID" required>
              <FieldInput value={companyId} onChange={(_, d) => setCompanyId(d.value)} readOnly={!isNew} appearance={isNew ? undefined : 'filled-darker'} />
            </Field>
            <Field label="Company Name" required>
              <FieldInput value={companyName} onChange={(_, d) => setCompanyName(d.value)} />
            </Field>
          </DialogContent>
          {!isNew && (
            <DialogActions position="start">
              <StatusToggle value={isActive} onChange={setIsActive} disabled={saving} />
            </DialogActions>
          )}
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              // ENABLED ON AN UNCHANGED FORM, as every References editor is
              // (D-170; QA 2026-10-04 B-03): `save` closes it with
              // NOTHING_CHANGED rather than leaving a dead button.
              disabled={saving || !ready}
              onClick={() => void save()}
            >
              Save
            </Button>
          </DialogActions>
        </DialogBody></FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}

// ---- GL codes (References → Codes) ----------------------------------------
//
// THREE THINGS ARE MAINTAINED HERE, not one (owner item 10(d), 2026-08-31):
// an Account (an `accounts` row), a Department (a `departments` row), and
// the Account & Department combination they compose (a `codes` row). The
// owner asked for all three to be creatable independently, which reverses the
// earlier design in which a half arrived only with a code — `docs/decisions.md`
// D-69 records the reversal and D-44 is what it supersedes.
//
// So the tab carries a VIEW switcher as well as a table: the combined view is
// the one that was always here (Code · Account · Department over all 269
// codes), and the two half views exist because a half authored on its own
// has no code to be decomposed out of and would otherwise be invisible.

/** One decomposed row: the code plus its two halves' ids (nullable only
    if `code_id` is somehow malformed, which the shape check on write rules
    out in practice). */
interface CodeRow {
  code: Code
  departmentId: string | null
  accountId: string | null
}

/** Which table the Codes tab is showing. `combined` is the view that predates
    owner item 10 and still lists every code — and is what "Code" now
    NAMES: an Account paired with a Department (owner, 2026-09-01).

    The stored values are frozen, `combined` included. Only the labels moved. */
type CodesView = 'combined' | 'accounts' | 'departments'

// Bare nouns, not "View: …". The label had to say "View" while the control was
// a dropdown in a row of dropdowns; the segmented control says it by being one
// (owner, 2026-09-01 — see `components/SegmentedControl.tsx`).
const CODES_VIEW_LABEL: Record<CodesView, string> = {
  accounts: 'Accounts',
  departments: 'Departments',
  combined: 'Codes',
}

/** Left to right, and the order the arrow keys walk. `combined` is last and is
    what the tab opens on. */
const CODES_VIEWS: readonly { value: CodesView; label: string }[] = [
  { value: 'accounts', label: CODES_VIEW_LABEL.accounts },
  { value: 'departments', label: CODES_VIEW_LABEL.departments },
  { value: 'combined', label: CODES_VIEW_LABEL.combined },
]

/** What the Add / Edit Reference dialog is maintaining. `combination` is a
    Code — an Account and a Department together; the other two are one of those
    halves in its own right. Stored values frozen; the labels are
    `CODE_MODE_LABEL`. */
type CodeMode = 'combination' | 'account' | 'department'

/** What the dialog was opened on. On `new` the dialog's own "What are you
    adding?" control decides which of the three it is, and starts unset —
    nothing is preselected (owner, 2026-09-01). */
type CodeEdit =
  | { kind: 'new' }
  | { kind: 'code'; code: Code }
  | { kind: 'account'; account: Account }
  | { kind: 'department'; department: Department }

/** `view` is owned by `MasterData` since 2026-09-14 - the switcher that sets
    it renders in the tab row, not in this manager. */
/** The key and the writer for one row of whichever Codes view is showing.
    Split out of the manager so the bulk hook reads one line per concern. */
// THE ROW'S OWN id, on all three views. It was the business key — the code
// id, or a half's own id — while the table
// keyed its checkbox by `id`, so every row checkbox on all three Codes
// sub-views was inert (QA wave B1, B1-01; the tester reached `Codes` and could
// not reach `Accounts` or `Departments`, which shared it).
function bulkKeyOf(row: Code | Account | Department): string {
  return row.id
}

/** The bulk dialog's subject, by view. */
const BULK_NOUN = { account: 'Account', department: 'Department', code: 'Code' } as const
const BULK_HEADING = { account: 'Accounts', department: 'Departments', code: 'Codes' } as const

/** The server's cascade as the dialog's sections: every record by name AND ID
    (owner, 2026-09-25 — D-182). A Code, Department or Account is its ID beside
    its name; a Coding Rule names each half it holds as `Name (ID)`, resolved
    from the cascade itself and then from the half masters the tab loaded. A
    blank or unknown name reads `(unnamed)`, never an empty row. */
function halfSections(
  cascade: InactiveCascade | null,
  accountNames: ReadonlyMap<string, string>,
  departmentNames: ReadonlyMap<string, string>,
): InactiveListSection[] {
  if (cascade === null) return []
  const names = cascadeHalfNames(cascade, accountNames, departmentNames)
  return [
    {
      heading: 'Codes',
      items: cascade.codes.map((c) => ({ key: c.code_id, id: c.code_id, primary: c.code_name?.trim() || UNNAMED })),
    },
    {
      heading: 'Departments',
      items: cascade.departments.map((d) => ({
        key: d.department_id,
        id: d.department_id,
        primary: d.department_name?.trim() || UNNAMED,
      })),
    },
    {
      heading: 'Accounts',
      items: cascade.accounts.map((a) => ({ key: a.account_id, id: a.account_id, primary: a.account_name?.trim() || UNNAMED })),
    },
    {
      heading: 'Coding Rules',
      items: cascade.coding_rules.map((r) => ({ key: r.id, ...codingRuleListItem(r, names) })),
    },
  ]
}

function bulkWrite(
  view: CodesView,
  row: Code | Account | Department,
  active: boolean,
): Promise<unknown> {
  const rowVersion = (row as { row_version: number }).row_version
  if (view === 'combined') {
    return updateCode((row as Code).code_id, { is_active: active, row_version: rowVersion })
  }
  if (view === 'accounts') {
    return updateAccount((row as Account).account_id, {
      is_active: active,
      row_version: rowVersion,
    })
  }
  return updateDepartment((row as Department).department_id, {
    is_active: active,
    row_version: rowVersion,
  })
}

function CodesManager({ view }: { view: CodesView }) {
  const s = useStyles()
  const toast = useToast()
  const [all, setAll] = useState<Code[] | null>(null)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [status, setStatus] = useState<StatusFilter>(DEFAULT_STATUS_FILTER)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [edit, setEdit] = useState<CodeEdit | null>(null)
  const [term, setTerm] = useState('')
  // A Code going Inactive, and what the server will make Inactive with it
  // (D-180): the Coding Rules naming its exact pair and any half it leaves with
  // no Active Code. `cascade` is null while that list is being read.
  const [deactivating, setDeactivating] = useState<{
    code: Code
    cascade: InactiveCascade | null
  } | null>(null)
  // THE RETIRE DIALOG'S OWN REFUSAL (QA wave B1, 2026-09-22, B1-02). It used to
  // go to `setError` above, which renders at the top of a page the modal has
  // locked from scrolling — so the message sat behind the dialog and the button
  // looked dead. Same rule as every editor on this screen: a refusal belongs
  // beside the attempt that caused it.
  const [deactivateError, setDeactivateError] = useState<string | null>(null)
  // AN ACCOUNT OR A DEPARTMENT GOING INACTIVE (owner, 2026-09-25 — D-175).
  // Never refused for being in use: the server makes its Active Codes, any
  // derived other half left without one, and the Coding Rules naming either
  // Inactive in the same write. The dialog lists them first; `cascade` is
  // null while that list is being read.
  const [halfDeactivating, setHalfDeactivating] = useState<{
    kind: 'account' | 'department'
    id: string
    name: string
    rowVersion: number
    cascade: InactiveCascade | null
  } | null>(null)
  const [halfDeactivateError, setHalfDeactivateError] = useState<string | null>(null)
  // THE BULK STRIP'S MARK INACTIVE ON ACCOUNTS OR DEPARTMENTS (D-175): the same
  // list first, for every selected half at once; each write then takes its own.
  const [bulkHalfDeactivating, setBulkHalfDeactivating] = useState<{
    kind: 'account' | 'department' | 'code'
    subjects: { id: string; name: string }[]
    cascade: InactiveCascade | null
  } | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      // THE SEARCH BUG (owner-reported: "I feel like some are missing in the
      // search results" — they were). The old reader took ONE
      // page of `limit: 200`; the chart has 269 codes, so 69 could never be
      // listed or found by any search term. `loadAllCodes` follows every
      // cursor — see `app/mock/tests/controller-codes.test.ts` for the
      // regression guard.
      //
      // The two half masters are loaded as ROWS, not as label maps: since owner
      // item 10 they are editable records in their own right, so the tab needs
      // `is_active`, `row_version` and `origin`, not just a name. The label maps
      // the combined view reads are derived from the same rows, so a rename can
      // never show two different answers on one screen.
      const [codes, accountRows, departmentRows] = await Promise.all([
        loadAllCodes(),
        loadAllAccounts().catch(() => [] as Account[]),
        loadAllDepartments().catch(() => [] as Department[]),
      ])
      setAll(codes)
      setAccounts(accountRows)
      setDepartments(departmentRows)
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const accountNames = useMemo(
    () => new Map(accounts.map((a) => [a.account_id, a.account_name] as const)),
    [accounts],
  )
  const departmentNames = useMemo(
    () => new Map(departments.map((d) => [d.department_id, d.department_name] as const)),
    [departments],
  )

  const rows = useMemo<CodeRow[]>(
    () =>
      (all ?? []).map((code) => {
        const halves = decomposeCodeId(code.code_id)
        return { code, departmentId: halves?.departmentId ?? null, accountId: halves?.accountId ?? null }
      }),
    [all],
  )

  // The Account and Department dropdown filters are GONE (owner, 2026-09-01).
  // They cross-filtered a table of pairs, and the tab no longer shows the two
  // halves as columns to cross-filter on: each view is ID + one name. The
  // search box still matches either half's label, so narrowing by "the Somerset
  // ones" is a search, not a dropdown.
  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    return rows.filter(({ code, departmentId, accountId }) => {
      if (!matchesStatus(status, code.is_active)) return false
      if (!q) return true
      const accountLabel = accountId ? (accountNames.get(accountId) ?? '') : ''
      const departmentLabel = departmentId ? (departmentNames.get(departmentId) ?? '') : ''
      return (
        code.code_id.toLowerCase().includes(q) ||
        code.code_name.toLowerCase().includes(q) ||
        accountLabel.toLowerCase().includes(q) ||
        departmentLabel.toLowerCase().includes(q)
      )
    })
  }, [rows, status, term, accountNames, departmentNames])

  const visibleAccounts = useMemo(() => {
    const q = term.trim().toLowerCase()
    return accounts
      .filter((a) => {
        if (!matchesStatus(status, a.is_active)) return false
        if (!q) return true
        return a.account_id.toLowerCase().includes(q) || a.account_name.toLowerCase().includes(q)
      })
      .sort((a, b) => a.account_name.localeCompare(b.account_name))
  }, [accounts, status, term])

  const visibleDepartments = useMemo(() => {
    const q = term.trim().toLowerCase()
    return departments
      .filter((d) => {
        if (!matchesStatus(status, d.is_active)) return false
        if (!q) return true
        return d.department_id.toLowerCase().includes(q) || d.department_name.toLowerCase().includes(q)
      })
      .sort((a, b) => a.department_name.localeCompare(b.department_name))
  }, [departments, status, term])

  // MAKING A CODE INACTIVE IS ONE SERVER WRITE (D-180). The server plans what
  // goes with it — the Coding Rules naming its exact pair, any half it leaves
  // with no Active Code, and the rules naming that half — and writes it in the
  // Code's own transaction. The dialog lists the server's own preview first.
  // It used to fetch the rules naming either half here and loop over them one
  // request each, which a refusal part-way could leave half done.
  async function beginDeactivate(code: Code) {
    setDeactivateError(null)
    setDeactivating({ code, cascade: null })
    try {
      const cascade = await previewInactiveCascade('code', code.code_id)
      setDeactivating((current) => (current && current.code.code_id === code.code_id ? { ...current, cascade } : current))
    } catch (e) {
      setDeactivateError(writeError(e, 'Could not check what uses it'))
    }
  }

  async function confirmDeactivate() {
    if (!deactivating || deactivating.cascade === null) return
    const { code, cascade } = deactivating
    setBusyId(code.id)
    setDeactivateError(null)
    try {
      await updateCode(code.code_id, { is_active: false, row_version: code.row_version })
      toast(inactiveToast('Code', cascadeCounts(cascade)))
      setDeactivating(null)
      await load()
    } catch (e) {
      setDeactivateError(writeError(e, 'Could not make the code Inactive'))
    } finally {
      setBusyId(null)
    }
  }

  async function beginHalfDeactivate(kind: 'account' | 'department', half: Account | Department) {
    const id = kind === 'account' ? (half as Account).account_id : (half as Department).department_id
    const name = kind === 'account' ? (half as Account).account_name : (half as Department).department_name
    setHalfDeactivateError(null)
    setHalfDeactivating({ kind, id, name, rowVersion: half.row_version, cascade: null })
    try {
      const cascade = await previewInactiveCascade(kind, id)
      setHalfDeactivating((current) => (current && current.id === id ? { ...current, cascade } : current))
    } catch (e) {
      setHalfDeactivateError(writeError(e, 'Could not check what uses it'))
    }
  }

  async function confirmHalfDeactivate() {
    if (!halfDeactivating || halfDeactivating.cascade === null) return
    const { kind, id, rowVersion, cascade } = halfDeactivating
    setBusyId(id)
    setHalfDeactivateError(null)
    try {
      // ONE WRITE. The server makes the Codes and Coding Rules Inactive in the
      // same transaction, so there is nothing here to loop over or roll back.
      if (kind === 'account') await updateAccount(id, { is_active: false, row_version: rowVersion })
      else await updateDepartment(id, { is_active: false, row_version: rowVersion })
      toast(inactiveToast(kind === 'account' ? 'Account' : 'Department', cascadeCounts(cascade)))
      setHalfDeactivating(null)
      await load()
    } catch (e) {
      setHalfDeactivateError(writeError(e, `Could not make the ${kind} Inactive`))
    } finally {
      setBusyId(null)
    }
  }

  // THE BULK STRIP'S MARK INACTIVE, ON ALL THREE VIEWS (D-175, D-180): the
  // same list first, for every selected record at once; each write then takes
  // its own cascade on the server.
  async function beginBulkDeactivate() {
    const kind: 'account' | 'department' | 'code' =
      view === 'accounts' ? 'account' : view === 'departments' ? 'department' : 'code'
    const subjects =
      kind === 'account'
        ? visibleAccounts
            .filter((r) => bulk.selection.has(bulk.keyOf(r)) && r.is_active !== false)
            .map((r) => ({ id: r.account_id, name: r.account_name }))
        : kind === 'department'
          ? visibleDepartments
              .filter((r) => bulk.selection.has(bulk.keyOf(r)) && r.is_active !== false)
              .map((r) => ({ id: r.department_id, name: r.department_name }))
          : visible
              .map((v) => v.code)
              .filter((c) => bulk.selection.has(bulk.keyOf(c)) && c.is_active !== false)
              .map((c) => ({ id: c.code_id, name: c.code_name || '(unnamed)' }))
    // Nothing Active is ticked: nothing goes with it, so there is nothing to list.
    if (subjects.length === 0) {
      bulk.mark(false)
      return
    }
    setHalfDeactivateError(null)
    setBulkHalfDeactivating({ kind, subjects, cascade: null })
    try {
      const merged = mergeCascades(await Promise.all(subjects.map((h) => previewInactiveCascade(kind, h.id))))
      // A half whose Active Codes are spread over SEVERAL selected records
      // loses its last one only once all of them go — no single preview sees it.
      const going = new Set([...merged.codes.map((c) => c.code_id), ...(kind === 'code' ? subjects.map((c) => c.id) : [])])
      const extraOf = (half: 'account' | 'department'): string[] => {
        if (half === kind) return []
        const listed = new Set(half === 'department' ? merged.departments.map((d) => d.department_id) : merged.accounts.map((a) => a.account_id))
        const rows =
          half === 'department'
            ? departments.map((d) => ({ id: d.department_id, is_active: d.is_active !== false, origin: d.origin ?? null }))
            : accounts.map((a) => ({ id: a.account_id, is_active: a.is_active !== false, origin: a.origin ?? null }))
        return combinedKnockOns(half, all ?? [], rows, going).filter((id) => !listed.has(id))
      }
      const extraDepartments = extraOf('department')
      const extraAccounts = extraOf('account')
      const extraRules = [
        ...(await Promise.all(extraDepartments.map((id) => previewInactiveCascade('department', id)))).flatMap((c, i) =>
          c.coding_rules.filter((r) => r.department_id === extraDepartments[i]),
        ),
        ...(await Promise.all(extraAccounts.map((id) => previewInactiveCascade('account', id)))).flatMap((c, i) =>
          c.coding_rules.filter((r) => r.account_id === extraAccounts[i]),
        ),
      ]
      const cascade = mergeCascades([
        merged,
        {
          codes: [],
          departments: extraDepartments.map((id) => ({ department_id: id, department_name: departmentNames.get(id) ?? id })),
          accounts: extraAccounts.map((id) => ({ account_id: id, account_name: accountNames.get(id) ?? id })),
          coding_rules: extraRules,
        },
      ])
      setBulkHalfDeactivating((current) => (current ? { ...current, cascade } : current))
    } catch (e) {
      setHalfDeactivateError(writeError(e, 'Could not check what uses them'))
    }
  }

  // Making a record Active again is a plain patch in the dialog's Status field;
  // only making one Inactive opens the confirmation, for the cascade it lists.

  // ONE SELECTION FOR THREE VIEWS. Only one table renders at a time, so the
  // hook is handed whichever list is showing and the write branches on the same
  // `view` — a Code row patches `codes`, a half row patches its own
  // master. Switching view re-keys the selection, which clears it: a tick made
  // against Accounts must not survive into Departments, where the same key
  // means a different record.
  const bulkRows: (Code | Account | Department)[] =
    view === 'combined'
      ? visible.map((v) => v.code)
      : view === 'accounts'
        ? visibleAccounts
        : visibleDepartments
  const bulk = useBulkMark({
    rows: bulkRows,
    keyOf: bulkKeyOf,
    isActive: (r) => (r as { is_active?: boolean }).is_active !== false,
    write: (r, active) => bulkWrite(view, r, active),
    noun: view === 'accounts' ? 'account' : view === 'departments' ? 'department' : 'code',
    onDone: (m) => {
      toast(m)
      void load()
    },
    onError: setError,
  })

  if (all === null) return <Spinner label="Loading codes…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      {/* The page's own tools live in the header's top-right cluster since
          2026-09-14 (owner) - see `components/ScreenActions.tsx`. */}
      <ScreenActions>
        {/* Nothing is preselected in the dialog any more, whichever view you
            press this in (owner, 2026-09-01) - the first thing it asks is what
            you are adding. */}
        <Tooltip content="Add Reference" relationship="label">
          <Button appearance="secondary" icon={<AddRegular />} onClick={() => setEdit({ kind: 'new' })} />
        </Tooltip>
        <ListFilters
          status={status}
          onStatusChange={setStatus}
          term={term}
          onTermChange={setTerm}
          searchPlaceholder="Search ID, account, or department…"
          ariaLabel="Codes"
        />
      </ScreenActions>
      {/* ALL THREE VIEWS ARE ID + ONE NAME (owner, 2026-09-01). The combined
          view's Account and Department columns are gone with the filters that
          narrowed them: a Code IS the pair, its ID spells both halves out
          (`NNNNNN-NN-NNN`), and its name is the chart's own name for the pair.
          The half views dropped their Combinations count for the same reason —
          two columns, the same two, on each. */}
      <BulkBar
        selection={bulk.selection}
        busy={bulk.busy}
        // Mark Inactive lists what goes with the selection first, on every
        // view (D-175, D-180); Mark Active writes straight away, because
        // nothing comes back with it.
        onMark={(active) => (active ? bulk.mark(true) : void beginBulkDeactivate())}
      />
      {view === 'combined' &&
        (visible.length === 0 ? (
          <Body1 className={s.mdHint}>{(all ?? []).length === 0 ? 'No codes to show.' : 'No codes match.'}</Body1>
        ) : (
          <MasterTable
            // The combined view's rows wrap a `Code`, so the selection's
            // own `keyOf` is composed rather than restated — there is still
            // exactly one function naming a row.
            bulk={{ selection: bulk.selection, keyOf: ({ code }) => bulk.keyOf(code) }}
            ariaLabel="Codes"
            columns={[
              { label: 'ID', width: 150, sort: ({ code }) => code.code_id },
              { label: 'Code', sort: ({ code }) => code.code_name || '' },
            ]}
            rows={visible}
            cells={({ code }) => [code.code_id, code.code_name || '(unnamed)']}
            rowLabel={({ code }) => code.code_name || code.code_id}
            inactive={({ code }) => !code.is_active}
            isBusy={({ code }) => busyId === code.id}
            onEdit={({ code }) => setEdit({ kind: 'code', code })}
            minWidth={560}
          />
        ))}
      {view === 'accounts' &&
        (visibleAccounts.length === 0 ? (
          <Body1 className={s.mdHint}>{accounts.length === 0 ? 'No accounts to show.' : 'No accounts match.'}</Body1>
        ) : (
          <MasterTable
            bulk={{ selection: bulk.selection, keyOf: (a: Account) => bulk.keyOf(a) }}
            ariaLabel="Accounts"
            columns={[
              { label: 'ID', width: 110, sort: (a) => a.account_id },
              { label: 'Account', sort: (a) => a.account_name },
            ]}
            rows={visibleAccounts}
            cells={(a) => [a.account_id, a.account_name]}
            rowLabel={(a) => a.account_name || a.account_id}
            inactive={(a) => a.is_active === false}
            isBusy={(a) => busyId === a.id}
            onEdit={(a) => setEdit({ kind: 'account', account: a })}
            minWidth={520}
          />
        ))}
      {view === 'departments' &&
        (visibleDepartments.length === 0 ? (
          <Body1 className={s.mdHint}>
            {departments.length === 0 ? 'No departments to show.' : 'No departments match.'}
          </Body1>
        ) : (
          <MasterTable
            bulk={{ selection: bulk.selection, keyOf: (d: Department) => bulk.keyOf(d) }}
            ariaLabel="Departments"
            columns={[
              { label: 'ID', width: 110, sort: (d) => d.department_id },
              { label: 'Department', sort: (d) => d.department_name },
            ]}
            rows={visibleDepartments}
            cells={(d) => [d.department_id, d.department_name]}
            rowLabel={(d) => d.department_name || d.department_id}
            inactive={(d) => d.is_active === false}
            isBusy={(d) => busyId === d.id}
            onEdit={(d) => setEdit({ kind: 'department', department: d })}
            minWidth={520}
          />
        ))}
      <CodeDialog
        row={edit}
        chart={all ?? []}
        accountNames={accountNames}
        departmentNames={departmentNames}
        onClose={() => setEdit(null)}
        onSaved={(message) => {
          setEdit(null)
          // NAME WHAT WAS SAVED (QA wave B1, B1-09). Adding an Account and
          // adding a Department both toasted `The code was added.` — and on
          // this tab a Code is one specific thing, the pair the two halves
          // make, so the sentence was naming the wrong record twice over.
          // The dialog builds the sentence (`savedToast`, QA wave B, B-05),
          // because only it knows whether Status moved.
          toast(message)
          void load()
        }}
        onDeactivateRequested={(code) => {
          setEdit(null)
          void beginDeactivate(code)
        }}
        onHalfDeactivateRequested={(kind, half) => {
          setEdit(null)
          void beginHalfDeactivate(kind, half)
        }}
      />

      {/* THE "MAKE … INACTIVE?" FAMILY (D-175, D-176): one dialog for a Code
          and for either half, listing by name what goes Inactive with it. */}
      <MakeInactiveDialog
        open={!!deactivating}
        noun="Code"
        intro={
          deactivating
            ? `Make ${nameWithId(deactivating.code.code_name, deactivating.code.code_id)} Inactive? It stops matching new lines.${
                deactivating.cascade && cascadeIsEmpty(deactivating.cascade) ? ' Nothing else goes with it.' : ' These go Inactive with it:'
              }`
            : ''
        }
        loading={deactivating?.cascade === null}
        sections={halfSections(deactivating?.cascade ?? null, accountNames, departmentNames)}
        notes={
          deactivating
            ? [
                IN_PROGRESS_NOTE,
                CODE_REACTIVATION_NOTE,
                ...(deactivating.cascade && deactivating.cascade.coding_rules.length > 0
                  ? ['To keep one of these rules, Cancel and re-point it on Rules → Codes first.']
                  : []),
              ]
            : []
        }
        confirmLabel={makeInactiveLabel('Code', deactivating?.cascade ? cascadeCounts(deactivating.cascade) : {})}
        busy={!!busyId}
        error={deactivateError}
        onConfirm={() => void confirmDeactivate()}
        onCancel={() => {
          setDeactivating(null)
          setDeactivateError(null)
        }}
      />
      <MakeInactiveDialog
        open={!!halfDeactivating}
        noun={halfDeactivating?.kind === 'department' ? 'Department' : 'Account'}
        intro={
          halfDeactivating
            ? `Make ${nameWithId(halfDeactivating.name, halfDeactivating.id)} Inactive? It stops being offered for new lines.${
                halfDeactivating.cascade && cascadeIsEmpty(halfDeactivating.cascade)
                  ? ' Nothing else uses it.'
                  : ' These go Inactive with it:'
              }`
            : ''
        }
        loading={halfDeactivating?.cascade === null}
        sections={halfSections(halfDeactivating?.cascade ?? null, accountNames, departmentNames)}
        notes={
          halfDeactivating
            ? [IN_PROGRESS_NOTE, reactivationNote(halfDeactivating.kind === 'department' ? 'Department' : 'Account')]
            : []
        }
        confirmLabel={makeInactiveLabel(
          halfDeactivating?.kind === 'department' ? 'Department' : 'Account',
          halfDeactivating?.cascade ? cascadeCounts(halfDeactivating.cascade) : {},
        )}
        busy={!!busyId}
        error={halfDeactivateError}
        onConfirm={() => void confirmHalfDeactivate()}
        onCancel={() => {
          setHalfDeactivating(null)
          setHalfDeactivateError(null)
        }}
      />
      <MakeInactiveDialog
        open={!!bulkHalfDeactivating}
        noun={bulkHalfDeactivating ? countOf(bulkHalfDeactivating.subjects.length, BULK_NOUN[bulkHalfDeactivating.kind]) : ''}
        intro={
          bulkHalfDeactivating
            ? `They stop being offered for new lines.${
                bulkHalfDeactivating.cascade && cascadeIsEmpty(bulkHalfDeactivating.cascade)
                  ? ' Nothing else uses them.'
                  : ' These go Inactive with them:'
              }`
            : ''
        }
        loading={bulkHalfDeactivating?.cascade === null}
        sections={
          bulkHalfDeactivating
            ? [
                {
                  heading: BULK_HEADING[bulkHalfDeactivating.kind],
                  items: bulkHalfDeactivating.subjects.map((h) => ({ key: h.id, id: h.id, primary: h.name?.trim() || UNNAMED })),
                },
                // A Code's cascade lists no Codes, so the heading above is the
                // only "Codes" section on that view.
                ...halfSections(bulkHalfDeactivating.cascade, accountNames, departmentNames),
              ]
            : []
        }
        notes={
          bulkHalfDeactivating
            ? [
                IN_PROGRESS_NOTE,
                bulkHalfDeactivating.kind === 'code'
                  ? CODE_REACTIVATION_NOTE
                  : reactivationNote(bulkHalfDeactivating.kind === 'account' ? 'Accounts' : 'Departments'),
              ]
            : []
        }
        confirmLabel={
          bulkHalfDeactivating
            ? makeInactiveLabel(
                countOf(bulkHalfDeactivating.subjects.length, BULK_NOUN[bulkHalfDeactivating.kind]),
                bulkHalfDeactivating.cascade ? cascadeCounts(bulkHalfDeactivating.cascade) : {},
              )
            : ''
        }
        busy={bulk.busy}
        error={bulkHalfDeactivating ? halfDeactivateError : null}
        onConfirm={() => {
          setBulkHalfDeactivating(null)
          bulk.mark(false)
        }}
        onCancel={() => {
          setBulkHalfDeactivating(null)
          setHalfDeactivateError(null)
        }}
      />
    </div>
  )
}

// ---- Composing a new code from two pickers (owner item 5, 2026-08-19) ------
//
// One picker per half, Department and Account (the names come from owner
// item 8). Each is a combobox over the corresponding
// active master. `EntityPicker` cannot do this — it is a server-paged searcher
// with no freeform mode — so this copies `VehicleDialog`'s own local freeform
// `Combobox` idiom (below), generalised from a plain string to an `{id,
// name}` pair. 43 departments and 99 accounts are affordable to hold
// in memory and filter client-side; no server round trip per keystroke.
//
// EXISTING-OR-NEW IS AN EXPLICIT CHOICE (owner item 10(a), 2026-08-31). It used
// to be INFERRED: type a name the master carries and you were picking one,
// type anything else and the picker silently flipped to defining a new half and
// grew two more fields. That read as the form glitching rather than as a
// decision, and a typo in a name was indistinguishable from an intent to
// author. The mode is now a radio pair the user sets, and the ID / Name
// fields that appear under "New" carry the half's own name — "Account ID",
// "Department Name" — so four fields on one form are never ambiguous about
// which half they belong to.
type HalfChoice = { kind: 'empty' } | { kind: 'existing' | 'new'; id: string; name: string }

/** Which side of the toggle a half is on. Explicit state, not inferred from
    whether the typed text happens to match a master row. */
type HalfMode = 'existing' | 'new'

/**
 * An ID field that lays the separator out as you type — owner item 10(b),
 * 2026-08-31.
 *
 * The masking itself is `maskAccountId` / `maskDepartmentId` in
 * `lib/codeId.ts`, pure and unit-tested; this component's only job is the
 * DOM half of it — reading the caret off the event and putting it back after
 * React re-renders with the masked value, so a paste or an edit in the middle
 * of the field does not throw the caret to the end.
 *
 * `useLayoutEffect`, not `useEffect`: the caret must be restored before the
 * browser paints, or it visibly jumps.
 */
function IdInput({
  value,
  onChange,
  placeholder,
  mask,
}: {
  value: string
  onChange: (next: string) => void
  placeholder: string
  mask: (next: string, caret: number, previous?: string) => MaskedId
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const pendingCaret = useRef<number | null>(null)
  useLayoutEffect(() => {
    const caret = pendingCaret.current
    if (caret === null) return
    pendingCaret.current = null
    const el = inputRef.current
    if (el && typeof el.setSelectionRange === 'function' && el.selectionStart !== caret) {
      el.setSelectionRange(caret, caret)
    }
  })
  return (
    <FieldInput
      ref={inputRef}
      value={value}
      placeholder={placeholder}
      inputMode="numeric"
      onChange={(ev, d) => {
        const target = ev.target as HTMLInputElement
        const caret = target.selectionStart ?? d.value.length
        const masked = mask(d.value, caret, value)
        pendingCaret.current = masked.caret
        onChange(masked.value)
      }}
    />
  )
}

function HalfPicker({
  fieldLabel,
  idPlaceholder,
  idShape,
  idHint,
  mask,
  options,
  value,
  onChange,
  mode,
  onModeChange,
  existingOnly,
  locked,
  lockedDisplay,
  dialogSurface,
}: {
  fieldLabel: string
  idPlaceholder: string
  idShape: RegExp
  idHint: string
  mask: (next: string, caret: number, previous?: string) => MaskedId
  options: HalfOption[]
  value: HalfChoice
  onChange: (v: HalfChoice) => void
  mode: HalfMode
  onModeChange: (m: HalfMode) => void
  /** Hides the existing-or-new toggle and pins the picker to `existing`
      (owner, 2026-09-01: a Code is composed from halves that already exist).
      The `new` side of the component is kept, not deleted — the owner said
      "for now", and the two half editors on this same tab are how a missing
      half gets authored in the meantime. */
  existingOnly?: boolean
  locked: boolean
  lockedDisplay: string
  dialogSurface: HTMLDivElement | null
}) {
  const s = useStyles()
  const df = useDialogFormStyles()
  const popupListboxClass = usePopupListboxClass()
  const byName = useMemo(() => new Map(options.map((o) => [o.name, o])), [options])
  // What the combobox INPUT shows in `existing` mode. Held locally so a partial
  // search term stays visible while `value` correctly reads as nothing picked —
  // the two are different facts, and conflating them is what made the old
  // implicit mode flip.
  const [typed, setTyped] = useState(value.kind === 'existing' ? value.name : '')
  useEffect(() => {
    setTyped(value.kind === 'existing' ? value.name : '')
    // Deliberately keyed on the MODE, not on `value`: re-syncing on every value
    // change would overwrite what the user is typing on the keystroke after it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  if (locked) {
    return (
      <Field label={fieldLabel}>
        <FieldInput value={lockedDisplay} readOnly appearance="filled-darker" />
      </Field>
    )
  }

  const idError = value.kind === 'new' && value.id.trim() !== '' && !idShape.test(value.id.trim())
  const lower = fieldLabel.toLowerCase()
  const effectiveMode: HalfMode = existingOnly ? 'existing' : mode

  // WHAT THE LIST SHOWS. Fluent's freeform Combobox does not filter — it only
  // scrolls to a label-prefix match — so before this the whole master was
  // rendered and typing an id moved nothing (owner, 2026-09-01).
  //
  // The one exception: once a row is PICKED the input holds its full name, and
  // filtering by that would collapse the list to the row already chosen, so
  // reopening it to look at a neighbour would mean clearing the field first.
  const shownOptions =
    value.kind === 'existing' && typed === value.name
      ? options
      : options.filter((o) => halfMatchesSearch(o, typed))

  return (
    <>
      {/* With the toggle hidden there is nothing between the label and the
          picker, so the picker's own Field carries the label instead of an
          empty one sitting above it. */}
      {!existingOnly && (
        <Field label={fieldLabel}>
          <RadioGroup
            layout="horizontal"
            value={mode}
            onChange={(_, d) => onModeChange(d.value as HalfMode)}
          >
            <Radio value="existing" label={`Pick an existing ${lower}`} />
            <Radio value="new" label={`Add a new ${lower}`} />
          </RadioGroup>
        </Field>
      )}
      {effectiveMode === 'existing' ? (
        <Field label={existingOnly ? fieldLabel : undefined}>
          <FieldCombobox
            freeform
            aria-label={fieldLabel}
            placeholder={`Search ${lower}s by name or ID…`}
            value={typed}
            selectedOptions={value.kind === 'existing' ? [value.id] : []}
            input={{ autoComplete: 'off' }}
            {...popupPositionProps(dialogSurface)}
            listbox={{ className: popupListboxClass }}
            onChange={(e) => {
              const text = e.target.value
              setTyped(text)
              const match = byName.get(text)
              // NOTHING IS INVENTED HERE ANY MORE. Text that names no row means
              // "nothing picked yet", not "define a new half" — that is what the
              // toggle above is for.
              onChange(match ? { kind: 'existing', id: match.id, name: match.name } : { kind: 'empty' })
            }}
            onOptionSelect={(_, d) => {
              const opt = options.find((o) => o.id === d.optionValue)
              if (opt) {
                setTyped(opt.name)
                onChange({ kind: 'existing', id: opt.id, name: opt.name })
              }
            }}
          >
            {/* An explicitly empty listbox, not an empty popup: a search that
                matches nothing should say so rather than look broken. */}
            {shownOptions.length === 0 ? (
              <Option key="__no_match" value="__no_match" text="" disabled>
                {`No ${lower}s match.`}
              </Option>
            ) : (
              shownOptions.map((o) => (
                <Option key={o.id} value={o.id} text={o.name}>
                  <span className={s.optionPrimary}>{o.name}</span>
                  <span className={s.optionSecondary}>{o.id}</span>
                </Option>
              ))
            )}
          </FieldCombobox>
        </Field>
      ) : (
        <div className={mergeClasses(df.grid2, s.halfNewRow)}>
          <Field
            label={`${fieldLabel} ID`}
            validationState={idError ? 'error' : 'none'}
            validationMessage={idError ? idHint : undefined}
          >
            <IdInput
              value={value.kind === 'new' ? value.id : ''}
              placeholder={idPlaceholder}
              mask={mask}
              onChange={(id) =>
                onChange({ kind: 'new', id, name: value.kind === 'new' ? value.name : '' })
              }
            />
          </Field>
          <Field label={`${fieldLabel} Name`}>
            <FieldInput
              value={value.kind === 'new' ? value.name : ''}
              onChange={(_, d) =>
                onChange({ kind: 'new', id: value.kind === 'new' ? value.id : '', name: d.value })
              }
            />
          </Field>
        </div>
      )}
    </>
  )
}

/** The shape each half's id must have. Duplicated from `lib/codeId.ts`'s
    own anchored copies deliberately — these are the DIALOG's field validators,
    and they are shown to a user with `idHint` beside them. */
const ACCOUNT_ID_SHAPE = /^\d{2}-\d{3}$/
const DEPARTMENT_ID_SHAPE = /^\d{6}$/

// The three things the Codes tab maintains, named the way the tab now names
// them (owner, 2026-09-01): a Code IS the pair, so "Account & Department" /
// "Account only" / "Department only" became the three plain nouns.
const CODE_MODE_LABEL: Record<CodeMode, string> = {
  account: 'Account',
  department: 'Department',
  combination: 'Code',
}

// NO HELP TEXT UNDER ACCOUNT OR DEPARTMENT (owner, 2026-09-29, D-207: "get
// rid of the help text under Account and Department"). Add showed "Codes must
// be derived from existing Accounts…" under each picker, and Edit showed "A
// code's halves are its identity…" under each locked field. Both went: the
// pickers only offer existing halves, and a locked field says it is locked.
function CodeDialog({
  row,
  chart,
  accountNames,
  departmentNames,
  onClose,
  onSaved,
  onDeactivateRequested,
  onHalfDeactivateRequested,
}: {
  row: CodeEdit | null
  /** The whole loaded chart — for the duplicate-code check and the "second
      department" warning, both of which read every code, not one page. */
  chart: Code[]
  accountNames: ReadonlyMap<string, string>
  departmentNames: ReadonlyMap<string, string>
  onClose: () => void
  /** `saved` is WHICH of the three this dialog just wrote, so the caller can
      name it — an Account, a Department or a Code (QA wave B1, B1-09). */
  onSaved: (message: string) => void
  /** Status was set to Inactive on an active code — the manager takes it from
      here, because retiring a code lists the coding rules pointing at it. */
  onDeactivateRequested: (code: Code) => void
  /** Status was set to Inactive on an active Account or Department — the
      manager lists what goes Inactive with it before anything is written
      (D-175). */
  onHalfDeactivateRequested: (kind: 'account' | 'department', half: Account | Department) => void
}) {
  const s = useStyles()
  const df = useDialogFormStyles()
  // Fluent keeps the surface mounted through its close transition, so `row`
  // reads null for a frame or two while the dialog is STILL ON SCREEN. Reading
  // the body off it directly meant a closing "Add Reference" visibly flipped
  // to "Edit Reference" and grew a Status field on its way out. `open` still
  // tracks `row`; everything the body renders tracks the last real one.
  const [lastRow, setLastRow] = useState<CodeEdit | null>(null)
  useEffect(() => {
    if (row) setLastRow(row)
  }, [row])
  const shown = row ?? lastRow

  const isNew = shown?.kind === 'new'
  const existing = shown?.kind === 'code' ? shown.code : null
  const existingAccount = shown?.kind === 'account' ? shown.account : null
  const existingDepartment = shown?.kind === 'department' ? shown.department : null
  // WHICH OF THE THREE THINGS this dialog is maintaining (owner item 10(d)).
  // On an edit it is fixed by the row; on a create the user picks it, and
  // NOTHING IS PRESELECTED (owner, 2026-09-01) — `null` until they answer, so
  // the form below is empty and Save is disabled rather than the dialog opening
  // already committed to one of the three.
  const [mode, setMode] = useState<CodeMode | null>(null)
  const [codeName, setCodeName] = useState('')
  const [department, setDepartment] = useState<HalfChoice>({ kind: 'empty' })
  const [account, setAccount] = useState<HalfChoice>({ kind: 'empty' })
  const [departmentMode, setDepartmentMode] = useState<HalfMode>('existing')
  const [accountMode, setAccountMode] = useState<HalfMode>('existing')
  // The single-half form's own two fields.
  const [halfId, setHalfId] = useState('')
  const [halfLabel, setHalfLabel] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useDialogError(row)
  const [dialogSurface, setDialogSurface] = useState<HTMLDivElement | null>(null)
  const [departmentOptions, setDepartmentOptions] = useState<HalfOption[]>([])
  const [accountOptions, setAccountOptions] = useState<HalfOption[]>([])

  useEffect(() => {
    if (!row || !isNew) return
    let cancelled = false
    Promise.all([loadDepartments(), loadAccounts()])
      .then(([departments, accounts]) => {
        if (cancelled) return
        // `search_terms` comes along so this picker matches the same three
        // fields the API's own `q` does — see `halfMatchesSearch`.
        setDepartmentOptions(
          departments.map((d: Department) => ({
            id: d.department_id,
            name: d.department_name,
            searchTerms: d.search_terms,
          })),
        )
        setAccountOptions(
          accounts.map((a: Account) => ({
            id: a.account_id,
            name: a.account_name,
            searchTerms: a.search_terms,
          })),
        )
      })
      .catch(() => {
        // Leaving both lists empty still leaves the "Add a new …" side of each
        // toggle available — a half can be defined by id and name even if the
        // picker's own suggestions failed to load.
      })
    return () => {
      cancelled = true
    }
  }, [row, isNew])

  useEffect(() => {
    if (!row) return
    if (row.kind === 'new') {
      setMode(null)
      setCodeName('')
      setDepartment({ kind: 'empty' })
      setAccount({ kind: 'empty' })
      setDepartmentMode('existing')
      setAccountMode('existing')
      setHalfId('')
      setHalfLabel('')
      setIsActive(true)
      return
    }
    if (row.kind === 'account') {
      setMode('account')
      setHalfId(row.account.account_id)
      setHalfLabel(row.account.account_name)
      setIsActive(row.account.is_active !== false)
      return
    }
    if (row.kind === 'department') {
      setMode('department')
      setHalfId(row.department.department_id)
      setHalfLabel(row.department.department_name)
      setIsActive(row.department.is_active !== false)
      return
    }
    setMode('combination')
    setIsActive(row.code.is_active)
    setCodeName(row.code.code_name)
    const halves = decomposeCodeId(row.code.code_id)
    setDepartment(
      halves
        ? { kind: 'existing', id: halves.departmentId, name: departmentNames.get(halves.departmentId) ?? halves.departmentId }
        : { kind: 'empty' },
    )
    setAccount(
      halves
        ? { kind: 'existing', id: halves.accountId, name: accountNames.get(halves.accountId) ?? halves.accountId }
        : { kind: 'empty' },
    )
  }, [row, departmentNames, accountNames])

  const composedId = composeCodeId(
    department.kind === 'empty' ? null : department.id,
    account.kind === 'empty' ? null : account.id,
  )
  const departmentReady =
    department.kind === 'existing' ||
    (department.kind === 'new' && DEPARTMENT_ID_SHAPE.test(department.id.trim()) && department.name.trim() !== '')
  const accountReady =
    account.kind === 'existing' ||
    (account.kind === 'new' && ACCOUNT_ID_SHAPE.test(account.id.trim()) && account.name.trim() !== '')
  // The colliding row itself, not just the fact of it: "already exists" left
  // the reader to go and look up which code that ID belongs to (owner,
  // 2026-09-01). Naming it also makes the claim checkable on the spot.
  const duplicateExisting = useMemo(
    () =>
      isNew && mode === 'combination' && composedId !== null
        ? (chart.find((c) => c.code_id === composedId) ?? null)
        : null,
    [isNew, mode, composedId, chart],
  )

  // E2's client half — a WARNING, not a block: an existing Account that today
  // composes with EXACTLY ONE Department would compose with a second. An
  // account used by one department looks department-specific, so pairing it
  // with another is worth a glance; an account already spread over
  // fifteen is routine and a warning there is noise. Legitimate either way
  // (that is how a real new code is born). The server separately and always
  // refuses the one pairing that is never legal (88-880/88-885 with anything
  // but 190110) — see `capitalEquipmentMinimum` in the API.
  //
  // THE COUNT IS CHECKED, which it was not before 2026-09-01 (owner: "the
  // warning label language ... does not seem to be accurate"). It fired for any
  // department the account did not already have and then said "currently has
  // one department. This adds a second" regardless — so pairing Uniforms
  // (`67-506`, used by fifteen departments in the real chart) with a sixteenth
  // claimed it had one. The threshold now matches the sentence, and the
  // sentence names the department it is counting, so a reader can check it.
  const secondDepartmentWarning = useMemo(() => {
    if (!isNew || mode !== 'combination' || account.kind !== 'existing' || department.kind === 'empty') return null
    const only = soleDepartmentForAccount(chart, account.id, department.id)
    if (only === null) return null
    return `${account.name} is used by one department today (${departmentNames.get(only) ?? only}). This adds a second.`
  }, [isNew, mode, account, department, chart, departmentNames])

  const halfShape = mode === 'department' ? DEPARTMENT_ID_SHAPE : ACCOUNT_ID_SHAPE
  const halfIdError = halfId.trim() !== '' && !halfShape.test(halfId.trim())
  const halfName = mode === 'department' ? 'Department' : 'Account'
  const duplicateHalf =
    isNew &&
    mode !== null &&
    mode !== 'combination' &&
    halfShape.test(halfId.trim()) &&
    (mode === 'department' ? departmentNames : accountNames).has(halfId.trim())

  // Nothing is ready while "What are you adding?" is unanswered.
  const ready =
    mode === null
      ? false
      : mode === 'combination'
        ? codeName.trim() !== '' &&
          (isNew ? departmentReady && accountReady && composedId !== null && duplicateExisting === null : true)
        : halfLabel.trim() !== '' && halfShape.test(halfId.trim()) && !duplicateHalf

  async function saveHalf(saved: CodeMode) {
    const id = halfId.trim()
    const name = halfLabel.trim()
    if (isNew) {
      if (mode === 'department') await createDepartment({ department_id: id, department_name: name })
      else await createAccount({ account_id: id, account_name: name })
      onSaved(savedToast(CODE_MODE_LABEL[saved].toLowerCase(), { wasNew: true }))
      return
    }
    // EDIT. GOING INACTIVE IS NOT A PLAIN PATCH (D-175): the server makes the
    // half's Codes and Coding Rules Inactive with it, so the Status field hands
    // off to the manager, which lists them before anything is written — the
    // combination editor's own hand-off below. A name edit made in the same
    // sitting is applied first, and the row it returns carries the fresh
    // version that flow needs. Making it Active is a plain patch: nothing comes
    // back with it.
    const current = existingDepartment ?? existingAccount
    if (!current) return
    // Each half names its own name field, so the two writes cannot share one
    // `changes` object: an extra property type-checks and is silently ignored.
    const currentName = existingDepartment ? existingDepartment.department_name : (existingAccount?.account_name ?? '')
    const nameChanged = name !== currentName
    const statusChanged = isActive !== (current.is_active !== false)
    if (!nameChanged && !statusChanged) {
      onSaved(NOTHING_CHANGED)
      return
    }
    if (statusChanged && !isActive) {
      if (existingDepartment) {
        const renamed = nameChanged
          ? await updateDepartment(existingDepartment.department_id, {
              department_name: name,
              row_version: existingDepartment.row_version,
            })
          : existingDepartment
        onHalfDeactivateRequested('department', renamed)
      } else if (existingAccount) {
        const renamed = nameChanged
          ? await updateAccount(existingAccount.account_id, {
              account_name: name,
              row_version: existingAccount.row_version,
            })
          : existingAccount
        onHalfDeactivateRequested('account', renamed)
      }
      return
    }
    const status = statusChanged ? { is_active: isActive } : {}
    if (existingDepartment) {
      await updateDepartment(existingDepartment.department_id, {
        ...(nameChanged ? { department_name: name } : {}),
        ...status,
        row_version: current.row_version,
      })
    } else if (existingAccount) {
      await updateAccount(existingAccount.account_id, {
        ...(nameChanged ? { account_name: name } : {}),
        ...status,
        row_version: current.row_version,
      })
    }
    onSaved(
      savedToast(CODE_MODE_LABEL[saved].toLowerCase(), {
        activeBefore: current.is_active !== false,
        activeAfter: isActive,
      }),
    )
  }

  async function save() {
    if (!ready || mode === null) return
    setSaving(true)
    try {
      if (mode !== 'combination') {
        await saveHalf(mode)
        return
      }
      if (isNew && composedId) {
        await createCode({
          code_id: composedId,
          code_name: codeName.trim(),
          ...(department.kind === 'new' ? { department_name: department.name.trim() } : {}),
          ...(account.kind === 'new' ? { account_name: account.name.trim() } : {}),
        })
      } else if (existing) {
        const nameChanged = codeName.trim() !== existing.code_name
        const statusChanged = isActive !== existing.is_active
        if (!nameChanged && !statusChanged) {
          onSaved(NOTHING_CHANGED)
          return
        }
        // GOING INACTIVE IS NOT A PLAIN PATCH. Retiring a code still has to
        // surface the vendor coding rules pointing at either of its halves,
        // and the server can refuse the write outright (§4.5's in-flight
        // guard), so the Status field hands off to the manager's existing
        // retire flow rather than writing `is_active` here. Any name edit made
        // in the same sitting is applied first, and the row it returns carries
        // the fresh version that flow needs.
        if (existing.is_active && !isActive) {
          const current = nameChanged
            ? await updateCode(existing.code_id, {
                code_name: codeName.trim(),
                row_version: existing.row_version,
              })
            : existing
          onDeactivateRequested(current)
          return
        }
        await updateCode(existing.code_id, {
          ...(nameChanged ? { code_name: codeName.trim() } : {}),
          ...(!existing.is_active && isActive ? { is_active: true } : {}),
          row_version: existing.row_version,
        })
      }
      onSaved(
        savedToast(CODE_MODE_LABEL[mode].toLowerCase(), {
          wasNew: isNew,
          activeBefore: existing?.is_active,
          activeAfter: isActive,
        }),
      )
    } catch (e) {
      setSaveError(writeError(e, `Could not save ${CODE_MODE_LABEL[mode].toLowerCase()}`))
    } finally {
      setSaving(false)
    }
  }

  const existingHalves = existing ? decomposeCodeId(existing.code_id) : null

  return (
    <AppDialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface ref={setDialogSurface}>
        <FrozenWhileClosed open={!!row}><DialogBody>
          {/* Owner item 10(c), 2026-08-31 (D-70): every References editor says
              the same two words, the way every Rules editor says "Add Rule".
              Which record it is, is the tab you are on plus — here — the mode
              control below. */}
          <DialogTitle>{isNew ? 'Add Reference' : 'Edit Reference'}</DialogTitle>
          {/* Tall enough not to scroll (owner, 2026-09-01, the same request
              owner items 21–22 made of the category editor): Fluent's
              `DialogContent` ships `overflow-y: auto` under a max-height, and
              both have to go or it clips. */}
          <DialogContent className={mergeClasses(s.dialogNoScroll, df.stack, df.beforeActions)}>
            <ErrorBanner message={saveError} />
            {isNew && (
              // A DROPDOWN, not the radio row it replaced, and it opens with
              // NOTHING SELECTED (owner, 2026-09-01). `placeholder` is what
              // shows until one is picked; `value` is empty in that state, so
              // Fluent does not render a selected label.
              <Field label="What are you adding?">
                <Dropdown
                  placeholder="Choose one…"
                  aria-label="What are you adding?"
                  selectedOptions={mode ? [mode] : []}
                  value={mode ? CODE_MODE_LABEL[mode] : ''}
                  onOptionSelect={(_, d) => setMode((d.optionValue as CodeMode | undefined) ?? null)}
                >
                  <Option value="account">{CODE_MODE_LABEL.account}</Option>
                  <Option value="department">{CODE_MODE_LABEL.department}</Option>
                  <Option value="combination">{CODE_MODE_LABEL.combination}</Option>
                </Dropdown>
              </Field>
            )}
            {mode === null ? null : mode === 'combination' ? (
              <>
                {/* Account sits ABOVE Department (owner, 2026-08-31): the account is
                    the half a person knows first, and the department narrows it. */}
                <HalfPicker
                  fieldLabel="Account"
                  idPlaceholder="NN-NNN"
                  idShape={ACCOUNT_ID_SHAPE}
                  idHint="2 digits, a dash, 3 digits."
                  mask={maskAccountId}
                  options={accountOptions}
                  value={account}
                  onChange={setAccount}
                  mode={accountMode}
                  onModeChange={(m) => {
                    setAccountMode(m)
                    setAccount(m === 'new' ? { kind: 'new', id: '', name: '' } : { kind: 'empty' })
                  }}
                  existingOnly
                  locked={!isNew}
                  lockedDisplay={
                    existingHalves
                      ? `${accountNames.get(existingHalves.accountId) ?? existingHalves.accountId} (${existingHalves.accountId})`
                      : ''
                  }
                  dialogSurface={dialogSurface}
                />
                <HalfPicker
                  fieldLabel="Department"
                  idPlaceholder="NNNNNN"
                  idShape={DEPARTMENT_ID_SHAPE}
                  idHint="6 digits."
                  mask={maskDepartmentId}
                  options={departmentOptions}
                  value={department}
                  onChange={setDepartment}
                  mode={departmentMode}
                  onModeChange={(m) => {
                    setDepartmentMode(m)
                    setDepartment(m === 'new' ? { kind: 'new', id: '', name: '' } : { kind: 'empty' })
                  }}
                  existingOnly
                  locked={!isNew}
                  lockedDisplay={
                    existingHalves
                      ? `${departmentNames.get(existingHalves.departmentId) ?? existingHalves.departmentId} (${existingHalves.departmentId})`
                      : ''
                  }
                  dialogSurface={dialogSurface}
                />
                {secondDepartmentWarning && <Body1 className={s.dialogWarn}>{secondDepartmentWarning}</Body1>}
                {/* ID and Name side by side, the same two-column row the half
                    editors use for Account ID / Account Name (owner,
                    2026-09-01) — one shape for "here is the record you are
                    about to create", whichever of the three it is. The ID was
                    a caption under the Name field before, which read as a
                    footnote rather than as half of the identity.

                    It is READ-ONLY because it is DERIVED: the two pickers above
                    compose it. Empty rather than an em dash until both are
                    picked, so the placeholder can show the shape it will
                    take. */}
                <div className={mergeClasses(df.grid2, s.halfNewRow)}>
                  <Field label="Code ID">
                    <FieldInput
                      value={composedId ?? ''}
                      placeholder="NNNNNN-NN-NNN"
                      readOnly
                      appearance="filled-darker"
                    />
                  </Field>
                  <Field label="Code Name">
                    <FieldInput value={codeName} onChange={(_, d) => setCodeName(d.value)} />
                  </Field>
                </div>
                {duplicateExisting && (
                  <Body1 className={s.dialogWarn}>
                    {`A code with this ID already exists: ${duplicateExisting.code_name || '(unnamed)'}.`}
                  </Body1>
                )}
              </>
            ) : (
              <>
                <div className={mergeClasses(df.grid2, s.halfNewRow)}>
                  {/* "ID", not "Code" (owner, 2026-09-01): on this form the
                      user is adding an Account or a Department, and a Code is
                      the pair of them. The column that shows this value is
                      called ID too. */}
                  <Field
                    label={`${halfName} ID`}
                    validationState={halfIdError ? 'error' : 'none'}
                    validationMessage={
                      halfIdError
                        ? mode === 'department'
                          ? '6 digits.'
                          : '2 digits, a dash, 3 digits.'
                        : undefined
                    }
                  >
                    {isNew ? (
                      <IdInput
                        value={halfId}
                        placeholder={mode === 'department' ? 'NNNNNN' : 'NN-NNN'}
                        mask={mode === 'department' ? maskDepartmentId : maskAccountId}
                        onChange={setHalfId}
                      />
                    ) : (
                      // The ID is the business key: line items, coding rules
                      // and the export all carry it. Renaming it is not an edit,
                      // it is a different record — so the NAME is what this
                      // editor changes, which is the fix for a label typo that
                      // had nowhere to be corrected before owner item 10.
                      <FieldInput value={halfId} readOnly appearance="filled-darker" />
                    )}
                  </Field>
                  <Field label={`${halfName} Name`}>
                    <FieldInput value={halfLabel} onChange={(_, d) => setHalfLabel(d.value)} />
                  </Field>
                </div>
                {duplicateHalf && (
                  <Body1 className={s.dialogWarn}>
                    {/* `withArticle`, not a literal "A" — this line read
                        `A account with this ID already exists.` on the
                        Accounts view (QA wave B1, B1-09). */}
                    {`${sentenceCase(withArticle(halfName.toLowerCase()))} with this ID already exists.`}
                  </Body1>
                )}
              </>
            )}
          </DialogContent>
          {!isNew && mode !== null && (
            <DialogActions position="start">
              <StatusToggle
                value={isActive}
                onChange={setIsActive}
                disabled={saving}
                // On an Account or a Department only. THE NOUNS THE RIGHT WAY
                // ROUND (QA wave B1, B1-09): what uses an Account is a CODE or
                // a CODING RULE, never the reverse — built from `halfName` so it
                // names the half being changed. Never refused for being in use
                // (D-175): Save lists what goes Inactive with it first.
                title={
                  mode === 'combination'
                    ? undefined
                    : `Making ${withArticle(halfName)} Inactive also makes the Codes and Coding Rules that use it Inactive. Save lists them first.`
                }
              />
            </DialogActions>
          )}
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              Save
            </Button>
          </DialogActions>
        </DialogBody></FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}

// `defaults` (vendor coding rules) moved verbatim to the Rules screen
// (owner item 10a, 2026-08-13) — see RulesScreen.tsx.

// `senders` (intake allow-list + company routing) moved verbatim to the
// Rules screen (owner item 10a, 2026-08-13) — see RulesScreen.tsx.

// ---- Vehicles (the fleet master) -------------------------------------------
// The fleet is what vehicle tagging resolves against: a printed token is matched
// against Number / VIN / License Plate and the vehicle NUMBER is stored as the
// tag, so the number is the identifier that matters — required, and unique among
// active rows (the database enforces that; a collision comes back as
// `duplicate_key`).
//
// Status is the fleet owner's own vocabulary, stored verbatim, and since D-65 it
// is the ONLY input to our `is_active`: `In Service` is listed, `Out of Service`
// and `Sold` are not. Nobody toggles both — the owner's ruling, 2026-08-28.
//
// A `Sold` vehicle is therefore an inactive row, and stays taggable anyway,
// because a final invoice for a sold vehicle still has to be coded: entity
// resolution reads EVERY vehicle, never the active ones (`allVehicles`).
//
// Which is why this tab's Status filter is the FLEET'S three-way value and not
// the shared two-way `is_active` one every other References tab narrows on
// (a Status dropdown since 2026-09-01, a "Show retired" switch before that).
// Asking for Active or Inactive here would ask the reader a question the
// records do not answer, and narrowing to Active would hide every Out of
// Service and Sold vehicle — so marking a van Out of Service would make it
// vanish from the register that is the only place to put it back. The filter
// opens on In Service, this list's equivalent of "Active is the default"
// (owner, 2026-09-14: the filter here "is missing all of its pickers, plus
// its default to transportation's equivalent of active"), and All statuses
// is one click away. The tab carried no status filter at all until that date.
/** The one company every vehicle belongs to (owner item 20, 2026-08-28), which
    is why the editor has no Company field. Written on a create; the fleet
    spreadsheet import still writes whatever the file says. */
const VEHICLE_COMPANY_LABEL = FLEET_COMPANY_NAME

/** The fleet's own "listed" value, and this list's default filter. Reads off
    `VEHICLE_STATUSES` (`@ap/shared`) so the two cannot name different strings. */
const IN_SERVICE = VEHICLE_STATUSES[0]
const OUT_OF_SERVICE = VEHICLE_STATUSES[1]

/** What the fleet's two bulk states are CALLED — on the buttons and in the
    sentence the write reports. One constant, read by both, because they said
    different things (QA wave B1, B1-07). */
const FLEET_BULK_LABELS = { active: IN_SERVICE, inactive: OUT_OF_SERVICE }

function VehiclesManager() {
  const s = useStyles()
  const toast = useToast()
  const [all, setAll] = useState<Vehicle[] | null>(null)
  const [term, setTerm] = useState('')
  /** The fleet director's own three-way Status, empty for all of them. Opens on
      In Service, which is this list's equivalent of "Active is the default"
      (owner, 2026-09-14) — see the `ListFilters` call below. A multi-select,
      like the four below (owner, 2026-10-14). */
  const [fleetStatus, setFleetStatus] = useState<string[]>([IN_SERVICE])
  /** THE REST OF THE RECORD'S OWN FIELDS (owner, 2026-09-15: the filter here
      "is still missing about 7 of 8 fields"). Status was the only picker, and
      a fleet of several hundred vans is read by make, by model, by year and by
      centre far more often than by anything the search box was doing for them.
      Each is `''` for "all".

      THE THREE THAT ARE NOT HERE are Veh #, VIN and License Plate: their
      values are unique per row, so a dropdown over them would list the fleet
      once per vehicle and pick out exactly one — which is what the search box
      already does, across all three at once. Company is absent for the
      opposite reason: every vehicle belongs to `PACE Healthcare
      Transportation` (`VEHICLE_COMPANY_LABEL`), so the control would have one
      option and narrow nothing. */
  const [make, setMake] = useState<string[]>([])
  const [model, setModel] = useState<string[]>([])
  const [year, setYear] = useState<string[]>([])
  const [department, setDepartment] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [edit, setEdit] = useState<Vehicle | 'new' | null>(null)
  const [importOpen, setImportOpen] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      setAll(await loadVehiclesForManagement())
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const visible = useMemo(() => {
    const t = term.trim().toLowerCase()
    // THE WHOLE FLEET, always. `loadVehiclesForManagement` sends no `active`
    // filter either, so the server returns every row — see the header note.
    return (all ?? [])
      .filter(
        (v) =>
          !t ||
          v.number.toLowerCase().includes(t) ||
          (v.vin ?? '').toLowerCase().includes(t) ||
          (v.license_plate ?? '').toLowerCase().includes(t) ||
          (v.make ?? '').toLowerCase().includes(t) ||
          (v.model ?? '').toLowerCase().includes(t) ||
          (v.company_label ?? '').toLowerCase().includes(t) ||
          (v.department ?? '').toLowerCase().includes(t) ||
          (v.status ?? '').toLowerCase().includes(t),
      )
      .filter((v) => fleetStatus.length === 0 || fleetStatus.includes(v.status ?? ''))
      .filter((v) => make.length === 0 || make.includes(v.make ?? ''))
      .filter((v) => model.length === 0 || model.includes(v.model ?? ''))
      .filter((v) => year.length === 0 || year.includes(v.year ?? ''))
      .filter((v) => department.length === 0 || department.includes(v.department ?? ''))
  }, [all, term, fleetStatus, make, model, year, department])

  /** The values actually present in the fleet, for one picker. Read off the
      WHOLE fleet rather than the rows on screen, so picking a make does not
      empty the Model list of everything that make does not build — a filter
      that rewrites the other filters as you use it is how a reader ends up
      unable to get back. Blank values are dropped: "no department" is what the
      All option already means. */
  const optionsFor = useCallback(
    (read: (v: Vehicle) => string | null | undefined) =>
      [...new Set((all ?? []).map((v) => (read(v) ?? '').trim()).filter((x) => x !== ''))]
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map((value) => ({ value, label: value })),
    [all],
  )

  // MARK IN SERVICE / MARK OUT OF SERVICE, not Active / Inactive (owner,
  // 2026-09-14: "then offer In Service and Out of Service"). A vehicle's
  // listing is derived from this very column by a database CHECK
  // (`vehicles_active_follows_status`, D-65), so Active and Inactive are not
  // values this list can be asked for — but the fleet's own Status IS, and
  // setting it is exactly what a fleet manager does. `Sold` is deliberately
  // not one of the two: it is a fact about a disposal, not a listing decision,
  // and it stays a per-row edit in the row's own editor.
  const bulk = useBulkMark({
    rows: visible,
    keyOf: (v: Vehicle) => v.id,
    isActive: (v: Vehicle) => v.status === IN_SERVICE,
    write: (v: Vehicle, active: boolean) =>
      updateVehicle(v.id, {
        status: (active ? IN_SERVICE : OUT_OF_SERVICE) as Vehicle['status'],
        row_version: v.row_version,
      }),
    noun: 'vehicle',
    // THE FLEET'S WORDS IN THE SENTENCE TOO (QA wave B1, B1-07). The buttons
    // said `Mark 2 Out of Service` and the toast that followed said
    // `2 vehicles marked inactive.` — the one place on this tab that still
    // spoke the listing vocabulary the rest of it deliberately does not.
    // The same pair goes to `BulkBar` below, so they cannot drift.
    labels: FLEET_BULK_LABELS,
    onDone: (m) => {
      toast(m)
      void load()
    },
    onError: setError,
  })

  if (all === null) return <Spinner label="Loading fleet…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      {/* The page's own tools live in the header's top-right cluster since
          2026-09-14 (owner) - see `components/ScreenActions.tsx`. */}
      <ScreenActions>
        {/* `ArrowImport`, not `ArrowUpload` - see the Vendors tab's own note. */}
        <Tooltip content="Import" relationship="label">
          <Button appearance="secondary" icon={<ArrowImportRegular />} onClick={() => setImportOpen(true)} />
        </Tooltip>
        <Tooltip content="Add Reference" relationship="label">
          <Button appearance="secondary" icon={<AddRegular />} onClick={() => setEdit('new')} />
        </Tooltip>
        {/* THE FLEET'S OWN STATUS, not ours (owner, 2026-09-14: the filter here
            "is missing all of its pickers, plus its default to transportation's
            equivalent of active"). Every other References list narrows on
            `is_active` through the shared Status control; a vehicle's listing
            is DERIVED from this three-way value (D-65), so offering the shared
            two-way one here would ask the reader a question the records do not
            answer. `defaultValues` is what makes **In Service** this list's
            equivalent of "Active is the default", Reset included. */}
        <ListFilters
          term={term}
          onTermChange={setTerm}
          searchPlaceholder="Search number, VIN, plate, make, centre, or status…"
          ariaLabel="Vehicles"
          selects={[
            {
              multiple: true,
              label: 'Status',
              values: fleetStatus,
              onChange: setFleetStatus,
              options: VEHICLE_STATUSES.map((v) => ({ value: v, label: v })),
              defaultValues: [IN_SERVICE],
            },
            {
              multiple: true,
              label: 'Make',
              values: make,
              onChange: setMake,
              options: optionsFor((v) => v.make),
            },
            {
              multiple: true,
              label: 'Model',
              values: model,
              onChange: setModel,
              options: optionsFor((v) => v.model),
            },
            {
              multiple: true,
              label: 'Year',
              values: year,
              onChange: setYear,
              options: optionsFor((v) => v.year),
            },
            {
              multiple: true,
              label: 'Department',
              values: department,
              onChange: setDepartment,
              options: optionsFor((v) => v.department),
            },
          ]}
        />
      </ScreenActions>
      <BulkBar
        selection={bulk.selection}
        busy={bulk.busy}
        labels={FLEET_BULK_LABELS}
        onMark={bulk.mark}
      />
      {visible.length === 0 ? (
        <Body1 className={s.mdHint}>
          {(all ?? []).length === 0
            ? 'The fleet is empty. Until vehicles are added here, tagging a line with a vehicle has nothing to match and automatic vehicle resolution finds nothing.'
            : 'No vehicles match.'}
        </Body1>
      ) : (
        // Status here is the FLEET DIRECTOR'S OWN VALUE (`status`), not our
        // `is_active` (owner item 20, 2026-08-28: "'Active' is not one of the
        // Status options in the editor. The label should reflect the actual
        // value"). Since D-65 the second says nothing the first did not.
        // An unlisted row still dims, via `retired` — which now reads directly
        // off the status the same cell prints.
        <MasterTable
          bulk={bulk}
          ariaLabel="Vehicles"
          columns={[
            { label: 'Department', sort: (v) => v.department || '' },
            { label: 'Number', width: 110, sort: (v) => v.number || '' },
            { label: 'Make', sort: (v) => v.make || '' },
            { label: 'Model', sort: (v) => v.model || '' },
          ]}
          rows={visible}
          cells={(v) => [v.department || '—', v.number || '—', v.make || '—', v.model || '—']}
          rowLabel={(v) => `vehicle ${v.number || v.id}`}
          inactive={(v) => !v.is_active}
          onEdit={setEdit}
          statusCell={(v) => v.status || '—'}
          statusSort={(v) => v.status || ''}
          minWidth={640}
        />
      )}
      <VehicleDialog
        row={edit}
        known={all ?? []}
        onClose={() => setEdit(null)}
        onSaved={(message) => {
          setEdit(null)
          toast(message)
          void load()
        }}
      />
      <FleetImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onApplied={(summary) => {
          setImportOpen(false)
          toast(`The fleet was imported: ${summary}.`)
          void load()
        }}
      />
    </div>
  )
}

function VehicleDialog({
  row,
  known,
  onClose,
  onSaved,
}: {
  row: Vehicle | 'new' | null
  /** Every vehicle we hold — the source of the Company / Department / Status
      suggestions, so hand entry converges on the values already in use. */
  known: Vehicle[]
  onClose: () => void
  /** The toast, from `savedToast`, or `NOTHING_CHANGED`. */
  onSaved: (message: string) => void
}) {
  const df = useDialogFormStyles()
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  // One field per column in the fleet owner's listing, in their order.
  const [num, setNum] = useState('')
  const [vin, setVin] = useState('')
  const [plate, setPlate] = useState('')
  const [make, setMake] = useState('')
  const [model, setModel] = useState('')
  const [year, setYear] = useState('')
  // No Company field: every vehicle belongs to PACE Healthcare Transportation
  // (owner item 20, 2026-08-28), so the control was a single-valued choice.
  // `company_label` is left OUT of the update patch entirely — absent means
  // "leave unchanged", so whatever the fleet spreadsheet imported survives an
  // edit here — and set to the one company on a create.
  const [department, setDepartment] = useState('')
  const [status, setStatus] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useDialogError(row)
  // The dialog surface itself — same pattern as AssignmentsScreen's
  // `dialogSurface`, and simpler than resolving via `.closest` since this ref
  // IS the host: it's set directly on `DialogSurface` below, so there is no
  // ambiguity about which dialog it belongs to.
  const [dialogSurface, setDialogSurface] = useState<HTMLDivElement | null>(null)
  // Shared height/z-index for the freeform combobox listboxes below (Company /
  // Department / Status) — see `popupPositioning.ts`. The worst case for the
  // missing max-height: `company_label` alone can run to several dozen
  // distinct values across the fleet, uncapped and unscrolled without it.
  const popupListboxClass = usePopupListboxClass()

  useEffect(() => {
    if (!row) return
    const v = row === 'new' ? null : row
    setNum(v?.number ?? '')
    setVin(v?.vin ?? '')
    setPlate(v?.license_plate ?? '')
    setMake(v?.make ?? '')
    setModel(v?.model ?? '')
    setYear(v?.year ?? '')
    setDepartment(v?.department ?? '')
    // A NEW VEHICLE OPENS ON In Service (QA 2026-10-04 B-02). It opened blank,
    // and Save sent `status: ''`, which the server refuses — a hidden required
    // field behind a form that marks only Veh # required.
    setStatus(v?.status ?? NEW_VEHICLE_STATUS)
  }, [row])

  /** Distinct values already in the fleet, for the freeform suggestion lists.
      Seeded from the data rather than a hard-coded vocabulary, because the
      labels are the fleet owner's to invent. */
  const suggestions = useMemo(() => {
    const pick = (f: (v: Vehicle) => string | null | undefined) =>
      [...new Set(known.map((v) => (f(v) ?? '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b))
    return {
      department: pick((v) => v.department),
      status: pick((v) => v.status),
    }
  }, [known])

  // Only the number is required — it is the tag resolution stores. Everything
  // else mirrors their listing and may legitimately be blank.
  const ready = !!num.trim()

  async function save() {
    if (!ready) return
    setSaving(true)
    try {
      // `null` clears, so blanking a field here really blanks it — the same rule
      // the import follows for a blank cell in a column that is present.
      const orNull = (v: string) => (v.trim() === '' ? null : v.trim())
      const fields = {
        number: num.trim(),
        vin: orNull(vin),
        license_plate: orNull(plate),
        make: orNull(make),
        model: orNull(model),
        year: orNull(year),
        department: orNull(department),
        // Status is never null. The server takes one of `VEHICLE_STATUSES` and
        // refuses any other text with a field error.
        status: (status.trim() || NEW_VEHICLE_STATUS) as VehicleStatus,
      }
      // The one company, spelled in full — it is an identifier in a system of
      // record, not a label. Set on create only; an edit never touches it.
      // No `is_active` on either call: it is derived from `status` on the
      // server (D-65), and PATCH /vehicles/{id} refuses a body that carries one.
      if (isNew) await createVehicle({ ...fields, company_label: VEHICLE_COMPANY_LABEL })
      else if (existing) {
        // A Save that changed nothing writes nothing (D-170).
        if (nothingChanged(existing, fields)) {
          onSaved(NOTHING_CHANGED)
          return
        }
        await updateVehicle(existing.id, { ...fields, row_version: existing.row_version })
      }
      // A vehicle's standing is In Service / Out of Service, derived on the
      // server from its status (D-65), never Active / Inactive — so an edit is
      // "was saved." whatever changed.
      onSaved(savedToast('vehicle', { wasNew: isNew }))
    } catch (e) {
      setSaveError(writeError(e, 'Could not save vehicle'))
    } finally {
      setSaving(false)
    }
  }

  /** A freeform combobox: suggests what is already in use, accepts anything new —
      so a status or centre the fleet owner adds later needs no code change. */
  const freeform = (
    label: string,
    value: string,
    setValue: (v: string) => void,
    options: string[],
    placeholder?: string,
  ) => (
    <Field label={label}>
      <FieldCombobox
        freeform
        value={value}
        selectedOptions={value ? [value] : []}
        placeholder={placeholder}
        // mountNode (dialogSurface) keeps the listbox inside the dialog's focus
        // trap — portaled to body it reads as "the dropdown closes as fast as it
        // opens" — and popupPositionProps also fixes autoSize collapsing it to
        // one scrolling row (app/README.md). Falls back to inlinePopup only if
        // dialogSurface hasn't mounted yet. `listbox` supplies the z-index and
        // max-height that `mountNode` + `autoSize: false` no longer provide on
        // their own.
        {...popupPositionProps(dialogSurface)}
        listbox={{ className: popupListboxClass }}
        onChange={(e) => setValue(e.target.value)}
        onOptionSelect={(_, d) => setValue(d.optionValue ?? '')}
      >
        {options.map((o) => (
          <Option key={o} value={o} text={o}>
            {o}
          </Option>
        ))}
      </FieldCombobox>
    </Field>
  )

  return (
    <AppDialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface ref={setDialogSurface}>
        <FrozenWhileClosed open={!!row}><DialogBody>
          <DialogTitle>{isNew ? 'Add Reference' : 'Edit Reference'}</DialogTitle>
          <DialogContent className={mergeClasses(df.stack, df.beforeActions)}>
            <ErrorBanner message={saveError} />
            <div className={df.grid2}>
              <Field label="Veh #" required>
                <FieldInput value={num} onChange={(_, d) => setNum(d.value)} placeholder="e.g. 214" />
              </Field>
              <Field label="Year">
                <FieldInput value={year} onChange={(_, d) => setYear(d.value)} placeholder="e.g. 2019" />
              </Field>
              <Field label="VIN">
                <FieldInput value={vin} onChange={(_, d) => setVin(d.value)} />
              </Field>
              <Field label="License Plate">
                <FieldInput value={plate} onChange={(_, d) => setPlate(d.value)} />
              </Field>
              <Field label="Make">
                <FieldInput value={make} onChange={(_, d) => setMake(d.value)} placeholder="e.g. Ford" />
              </Field>
              <Field label="Model">
                <FieldInput value={model} onChange={(_, d) => setModel(d.value)} placeholder="e.g. Transit" />
              </Field>
              {freeform('Department', department, setDepartment, suggestions.department, 'operating centre')}
              {/* A CLOSED list, not the freeform combobox Department uses.
                  `vehicles.status` is a Postgres enum (`vehicle_status`) and
                  the route validates against `VEHICLE_STATUSES`, so a typed
                  "In Shop" was never storable — it came back 400 on Save. That
                  was survivable while Status was decoration; it is not now that
                  Status is the only input to the listing (D-65), so the control
                  offers exactly what the column accepts. Department stays
                  freeform: it is plain text with no enum behind it. */}
              <Field label="Status">
                <Dropdown
                  selectedOptions={[status]}
                  value={status}
                  onOptionSelect={(_, d) => setStatus(d.optionValue ?? status)}
                  mountNode={dialogSurface ?? undefined}
                  listbox={{ className: popupListboxClass }}
                  {...popupPositionProps(dialogSurface)}
                >
                  {VEHICLE_STATUSES.map((v) => (
                    <Option key={v} value={v}>
                      {v}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              {/* LISTING IS GONE FROM THIS DIALOG (owner, 2026-09-14). It was a
                  read-only mirror of the Status picked one field above, added
                  so the consequence of that pick was visible before Save — but
                  it is fully derived (D-65, `vehicleIsActiveFor`), so it showed
                  the reader a second field they could not set, restating what
                  they had just chosen. The derivation is unchanged; only its
                  echo in the editor is gone. */}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              Save
            </Button>
          </DialogActions>
        </DialogBody></FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}

// ---- Marketing / Transportation category vocabularies (owner item 11,
// 2026-08-19) ----------------------------------------------------------------
// Two new References tabs, one shared generic manager: `marketing_categories`
// and `transportation_categories` are byte-identical in shape (a `category`
// code and its `context` — the owner's own example, `A` means "newspaper
// ad"), so one component parametrized by which vocabulary it talks to avoids
// keeping two copies of list/search/retire/add/edit in sync. Copies the
// `VehiclesManager` / `VehicleDialog` pattern: list, search, show-retired
// toggle, add/edit dialog, soft retire via `is_active`.
type CategoryVocabRow = MarketingCategory | TransportationCategory

function CategoryVocabManager<T extends CategoryVocabRow>({
  kind,
  load,
  create,
  update,
}: {
  kind: 'Marketing' | 'Transportation'
  load: () => Promise<T[]>
  create: (body: { category: string; context: string }) => Promise<T>
  update: (id: string, body: { category?: string; context?: string; is_active?: boolean; row_version: number }) => Promise<T>
}) {
  const s = useStyles()
  const toast = useToast()
  const [all, setAll] = useState<T[] | null>(null)
  const [term, setTerm] = useState('')
  const [status, setStatus] = useState<StatusFilter>(DEFAULT_STATUS_FILTER)
  const [error, setError] = useState<string | null>(null)
  const [edit, setEdit] = useState<T | 'new' | null>(null)
  const kindLower = kind.toLowerCase()

  const loadAll = useCallback(async () => {
    setError(null)
    try {
      setAll(await load())
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
    // `load` is one of the module-level `loadMarketingCategoriesForManagement`
    // / `loadTransportationCategoriesForManagement` exports — a stable
    // reference, so this never re-fires on its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    void loadAll()
  }, [loadAll])

  const visible = useMemo(() => {
    const t = term.trim().toLowerCase()
    return (all ?? [])
      .filter((c) => matchesStatus(status, c.is_active))
      .filter((c) => !t || c.category.toLowerCase().includes(t) || c.context.toLowerCase().includes(t))
  }, [all, status, term])

  const bulk = useBulkMark({
    rows: visible,
    keyOf: (r: T) => String(r.id),
    isActive: (r: T) => r.is_active !== false,
    write: (r: T, active: boolean) => update(String(r.id), { is_active: active, row_version: r.row_version }),
    noun: categoryNoun(kind),
    onDone: (m) => {
      toast(m)
      // `loadAll`, NOT the `load` PROP (QA wave B1, B1-04). `load` is this
      // component's `() => Promise<T[]>` input; calling it fetched the rows and
      // threw them away, so a bulk Mark Active toasted success over a table
      // still showing the old status — and the next action on that row was
      // refused with "This record changed since you loaded it", because the
      // stale row carried a stale `row_version`. Every other tab's `onDone`
      // names its own reload; this is the one where a prop shadowed it.
      void loadAll()
    },
    onError: setError,
  })

  if (all === null) return <Spinner label={`Loading ${kindLower} categories…`} />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      {/* The page's own tools live in the header's top-right cluster since
          2026-09-14 (owner) - see `components/ScreenActions.tsx`. */}
      <ScreenActions>
        <Tooltip content="Add Reference" relationship="label">
          <Button appearance="secondary" icon={<AddRegular />} onClick={() => setEdit('new')} />
        </Tooltip>
        <ListFilters
          status={status}
          onStatusChange={setStatus}
          term={term}
          onTermChange={setTerm}
          searchPlaceholder="Search category or definition…"
          // The tab is called Marketing and Transportation now (owner,
          // 2026-09-14), so the Filter tooltip this builds says so too — it
          // read "Filter Marketing Categories" for one round, which put back
          // the word the rename had just taken out.
          ariaLabel={kind}
        />
      </ScreenActions>
      {visible.length === 0 ? (
        <Body1 className={s.mdHint}>
          {(all ?? []).length === 0 ? `No ${kindLower} categories yet.` : 'No categories match.'}
        </Body1>
      ) : (
        // "Definition" is the owner's label for what the row calls `context`
        // (owner items 21 and 22, 2026-08-28). The column header changed; the
        // field name on the wire did not.
        <>
        <BulkBar selection={bulk.selection} busy={bulk.busy} onMark={bulk.mark} />
        <MasterTable
          bulk={bulk}
          ariaLabel={`${kind} categories`}
          columns={[
            { label: 'Category', width: 180, sort: (c) => c.category },
            { label: 'Definition', sort: (c) => c.context },
          ]}
          rows={visible}
          cells={(c) => [c.category, c.context]}
          rowLabel={(c) => c.category}
          inactive={(c) => !c.is_active}
          onEdit={setEdit}
          minWidth={520}
        />
        </>
      )}
      <CategoryVocabDialog
        kind={kind}
        row={edit}
        create={create}
        update={update}
        onClose={() => setEdit(null)}
        onSaved={(message) => {
          setEdit(null)
          toast(message)
          void loadAll()
        }}
      />
    </div>
  )
}

function CategoryVocabDialog<T extends CategoryVocabRow>({
  kind,
  row,
  create,
  update,
  onClose,
  onSaved,
}: {
  kind: 'Marketing' | 'Transportation'
  row: T | 'new' | null
  create: (body: { category: string; context: string }) => Promise<T>
  update: (
    id: string,
    body: { category?: string; context?: string; is_active?: boolean; row_version: number },
  ) => Promise<T>
  onClose: () => void
  /** The toast, from `savedToast`. */
  onSaved: (message: string) => void
}) {
  const s = useStyles()
  const df = useDialogFormStyles()
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  const [category, setCategory] = useState('')
  const [context, setContext] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useDialogError(row)

  useEffect(() => {
    if (!row) return
    const r = row === 'new' ? null : row
    setCategory(r?.category ?? '')
    setContext(r?.context ?? '')
    setIsActive(r?.is_active ?? true)
  }, [row])

  const ready = category.trim() !== '' && context.trim() !== ''

  async function save() {
    if (!ready) return
    setSaving(true)
    try {
      if (isNew) await create({ category: category.trim(), context: context.trim() })
      else if (existing) {
        // Status rides in the same patch — the row's Retire button is gone.
        const next = { category: category.trim(), context: context.trim(), is_active: isActive }
        // A Save that changed nothing writes nothing (D-170).
        if (nothingChanged(existing, next)) {
          onSaved(NOTHING_CHANGED)
          return
        }
        await update(existing.id, { ...next, row_version: existing.row_version })
      }
      onSaved(
        savedToast(categoryNoun(kind), {
          wasNew: isNew,
          activeBefore: existing?.is_active,
          activeAfter: isActive,
        }),
      )
    } catch (e) {
      setSaveError(writeError(e, `Could not save ${categoryNoun(kind)}`))
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppDialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <FrozenWhileClosed open={!!row}><DialogBody>
          {/* 'Add Reference' / 'Edit Reference' on BOTH vocabularies — the tab
              you are on already says which one (owner items 21 and 22; the
              wording is owner item 10(c), 2026-08-31, D-70, which made every
              References editor say the same two words the way every Rules
              editor says 'Add Rule'). */}
          <DialogTitle>{isNew ? 'Add Reference' : 'Edit Reference'}</DialogTitle>
          <DialogContent className={mergeClasses(s.dialogNoScroll, df.stack, df.beforeActions)}>
            <ErrorBanner message={saveError} />
            <Field
              label="Category"
              required
              hint={
                kind === 'Marketing'
                  ? 'This code will appear in the description line of every item imported into Point Click Care.'
                  : undefined
              }
            >
              <FieldInput
                value={category}
                onChange={(_, d) => setCategory(d.value)}
                placeholder={kind === 'Marketing' ? 'A' : 'Repair'}
                maxLength={40}
              />
            </Field>
            {/* "Definition" is the owner's word for the `context` column. */}
            <Field label="Definition" required>
              <FieldInput
                value={context}
                onChange={(_, d) => setContext(d.value)}
                placeholder={kind === 'Marketing' ? 'Newspaper ad' : 'Any replacement or refurbishment of parts'}
                maxLength={200}
              />
            </Field>
          </DialogContent>
          {!isNew && (
            <DialogActions position="start">
              <StatusToggle value={isActive} onChange={setIsActive} disabled={saving} />
            </DialogActions>
          )}
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              Save
            </Button>
          </DialogActions>
        </DialogBody></FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}

function MarketingCategoriesManager(): ReactElement {
  return (
    <CategoryVocabManager
      kind="Marketing"
      load={loadMarketingCategoriesForManagement}
      create={createMarketingCategory}
      update={updateMarketingCategory}
    />
  )
}

function TransportationCategoriesManager(): ReactElement {
  return (
    <CategoryVocabManager
      kind="Transportation"
      load={loadTransportationCategoriesForManagement}
      create={createTransportationCategory}
      update={updateTransportationCategory}
    />
  )
}

// ---- Fleet spreadsheet import ----------------------------------------------
// References → Vehicles' import. A record ABSENT from the file is reported and
// left alone: a vehicle's listing follows the Status on its own record (D-65),
// so an import never makes one Inactive — a filtered or partial export cannot
// quietly retire half the fleet (`domain/fleetImport.ts`).
function FleetImportDialog({
  open,
  onClose,
  onApplied,
}: {
  open: boolean
  onClose: () => void
  onApplied: (summary: string) => void
}) {
  const flow = useImportFlow({
    preview: previewVehicleImport,
    commit: commitVehicleImport,
    summarize: (result) => {
      const bits = [`${result.added} added`, `${result.updated} updated`]
      if (result.unchanged) bits.push(`${result.unchanged} unchanged`)
      if (result.absent_untouched) bits.push(`${result.absent_untouched} left alone`)
      return sentenceList(bits)
    },
    onApplied,
  })
  const preview = flow.preview
  const mapping = preview?.mapping
  const writes = preview ? preview.adds.length + preview.updates.length : 0

  return (
    <ImportDialog
      open={open}
      onClose={onClose}
      flow={flow}
      accept=".xlsx"
      dropLabel="Drag the spreadsheet here, or click to browse"
      dropHint="Excel workbook (.xlsx)"
      writes={writes}
      intro={
        // NOT THE VENDOR IMPORT'S SECOND SENTENCE: a vehicle's listing is
        // derived from its Status (`vehicles_active_follows_status`, D-65), so
        // "marked inactive" would be a claim about the vehicle the file did not
        // make. The absent rows are listed in the preview instead.
        <Body1>
          Import here the full vehicle list from PACE Healthcare Transportation. Vehicles
          missing from the file are listed below and left exactly as they are — a
          vehicle&rsquo;s listing follows the Status on its own record, so an import never
          makes one Inactive.
        </Body1>
      }
    >
      {preview && mapping && (
        <>
          <Caption1>
            <strong>{flow.fileName}</strong> · sheet “{preview.sheet_name}” · header row {preview.header_row}
          </Caption1>
          <ImportCounts>
            <Badge appearance="filled" color="brand">{preview.adds.length} to add</Badge>
            <Badge appearance="filled" color="success">{preview.updates.length} to update</Badge>
            <Badge appearance="tint" color="informative">{preview.unchanged_count} unchanged</Badge>
            {preview.absent.length > 0 && (
              <Badge appearance="tint" color="warning">{preview.absent.length} not in this file</Badge>
            )}
          </ImportCounts>
          {/* Fields the file did not carry are LEFT ALONE on every row it
              touches rather than blanked — a notice, not an error. */}
          {mapping.missing.length > 0 && (
            <ImportNotice>
              This file has no column for{' '}
              <strong>{sentenceList(mapping.missing.map((f) => FLEET_FIELD_LABEL[f]))}</strong>. Those values are
              left exactly as they are on every row this import touches — nothing is blanked.
            </ImportNotice>
          )}
          {mapping.unmapped.length > 0 && (
            <ImportNotice>
              These columns were not recognised and will be ignored: <strong>{sentenceList(mapping.unmapped)}</strong>.
              Everything else imports normally.
            </ImportNotice>
          )}
          {!mapping.deterministic && (
            <ImportNotice>
              The column names in this file did not all match cleanly, so check the changes below carefully before
              applying.
            </ImportNotice>
          )}
          {preview.duplicates.length > 0 && (
            <ImportNotice>
              The file lists these vehicle numbers more than once:{' '}
              <strong>{sentenceList(preview.duplicates.map((d) => `${d.number} (rows ${sentenceList(d.rows.map(String))})`))}</strong>.
              The last row wins for each.
            </ImportNotice>
          )}
          <SkippedRows skipped={preview.skipped} />
          <ImportNames
            heading="Unchanged Vehicles Not in This File"
            names={preview.absent.map((v) => v.number)}
            limit={40}
            note="An import makes nothing Inactive. If any of these have genuinely left the fleet, make them Inactive one at a time."
          />
          <ImportNames
            heading="New Vehicles"
            names={preview.adds.map((a) => a.values.number ?? `row ${a.row}`)}
            limit={40}
          />
          <ImportChanges
            updates={preview.updates}
            keyOf={(u) => u.vehicle_id}
            title={(u) => `#${u.number}`}
            changes={(u) => u.changes}
            fieldLabel={(f: FleetField) => FLEET_FIELD_LABEL[f]}
          />
          <ImportHint>
            Columns read: {FLEET_FIELDS.filter((f) => mapping.by_field[f] !== undefined)
              .map((f: FleetField) => FLEET_FIELD_LABEL[f])
              .join(', ') || 'none'}
            .
          </ImportHint>
        </>
      )}
    </ImportDialog>
  )
}

// References → Vendors' Point Click Care list import (owner item 12,
// 2026-08-19). Three things differ from the fleet's because the real file
// forces them: a `.csv` with a 4 MiB cap checked here too, so an oversized file
// fails before it is sent; no "unmapped columns" warning, because by design only
// a few of the file's columns are read; and `absent` can be in the thousands,
// so it is a prominent count and a short sample. ABSENT MEANS INACTIVE (owner,
// 2026-08-28): the list is the vendor list.
function VendorImportDialog({
  open,
  onClose,
  onApplied,
}: {
  open: boolean
  onClose: () => void
  onApplied: (summary: string) => void
}) {
  const s = useStyles()
  const df = useDialogFormStyles()
  const flow = useImportFlow({
    preview: previewVendorImport,
    commit: commitVendorImport,
    summarize: (result) => {
      const bits = [`${result.added} added`, `${result.updated} updated`]
      if (result.unchanged) bits.push(`${result.unchanged} unchanged`)
      if (result.deactivated) bits.push(`${result.deactivated} marked inactive`)
      return sentenceList(bits)
    },
    onApplied,
    tooLarge: {
      maxBytes: VENDOR_IMPORT_MAX_BYTES,
      message: 'That file is larger than 4 MiB, this import’s limit. Trim it or split it and try again.',
    },
  })
  // The second press, and only for the shape of a mistaken upload — see
  // `lib/vendorImportGuard.ts` (QA wave B1, 2026-09-22, B1-07).
  const [confirmGut, setConfirmGut] = useState(false)
  const preview = flow.preview

  // Deactivations are writes too, and on the real file the largest group.
  const writes = preview ? preview.adds.length + preview.updates.length + preview.absent_total : 0
  const activeBefore = preview
    ? activeVendorsBefore({
        unchanged_count: preview.unchanged_count,
        updates: preview.updates.length,
        absent_total: preview.absent_total,
      })
    : 0
  const wouldGut = preview ? importWouldGutTheList(preview.absent_total, activeBefore) : false
  const mappedFields = preview
    ? VENDOR_IMPORT_FIELDS.filter((f) => preview.mapping.by_field[f] !== undefined)
    : []

  return (
    <ImportDialog
      open={open}
      onClose={() => {
        setConfirmGut(false)
        onClose()
      }}
      flow={flow}
      accept=".csv"
      dropLabel="Drag the CSV here, or click to browse"
      dropHint="CSV, up to 4 MB"
      writes={writes}
      onApply={() => (wouldGut ? setConfirmGut(true) : void flow.apply())}
      intro={
        <Body1>
          Import here the full vendor list from Point Click Care. Vendors missing from the file will be marked
          as inactive here in this system.
        </Body1>
      }
      // THE SECOND PRESS sits inside the import's surface: a Fluent `Dialog`
      // reads its children positionally, and a second child of the outer one
      // became its "trigger" (QA re-verification, 2026-09-22, N-01).
      nested={
        <AppDialog open={confirmGut} onOpenChange={(_, d) => !d.open && setConfirmGut(false)}>
          <DialogSurface>
            <FrozenWhileClosed open={confirmGut}><DialogBody>
              <DialogTitle>Do you want to mark most of the vendor list inactive?</DialogTitle>
              <DialogContent className={mergeClasses(df.stack, df.beforeActions)}>
                <Body1 className={s.confirmParagraph}>
                  This file names {preview ? preview.unchanged_count + preview.updates.length : 0} of
                  the {activeBefore} vendors that are active now, so {preview?.absent_total ?? 0}{' '}
                  would be marked inactive.
                </Body1>
                <Body1 className={s.confirmParagraph}>
                  Nothing is deleted and nothing loses its history, but there is no undo for the set:
                  an inactive vendor is set back to Active one at a time. If this file was meant to
                  be the whole list from Point Click Care, check that it is complete before going on.
                </Body1>
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setConfirmGut(false)} disabled={flow.applying}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  disabled={flow.applying}
                  onClick={() => {
                    setConfirmGut(false)
                    void flow.apply()
                  }}
                >
                  Mark {preview?.absent_total ?? 0} Inactive
                </Button>
              </DialogActions>
            </DialogBody></FrozenWhileClosed>
          </DialogSurface>
        </AppDialog>
      }
    >
      {preview && (
        <>
          <Caption1>
            <strong>{flow.fileName}</strong>
          </Caption1>
          <ImportCounts>
            <Badge appearance="filled" color="brand">{preview.adds.length} to add</Badge>
            <Badge appearance="filled" color="success">{preview.updates.length} to update</Badge>
            <Badge appearance="tint" color="informative">{preview.unchanged_count} unchanged</Badge>
            {preview.absent_total > 0 && (
              <Badge appearance="filled" color="danger">
                {preview.absent_total} to mark inactive
              </Badge>
            )}
          </ImportCounts>
          {/* What is read, quietly: most of the real file's columns are never
              read, by design, so an "unmapped" warning would be noise. */}
          <ImportHint>
            Reads {mappedFields.map((f) => VENDOR_IMPORT_FIELD_LABEL[f]).join(', ') || 'nothing recognisable'}.
            Every other column in the file is ignored.
          </ImportHint>
          {preview.duplicates.length > 0 && (
            <ImportNotice>
              The file lists these vendor codes more than once:{' '}
              <strong>
                {sentenceList(preview.duplicates.map((d) => `${d.vendor_id} (rows ${sentenceList(d.rows.map(String))})`))}
              </strong>
              . The last row wins for each, and an earlier row fills a cell the last row leaves blank.
            </ImportNotice>
          )}
          <SkippedRows skipped={preview.skipped} />
          {/* The COUNT is the point: the largest write the import makes, seen
              before committing. A short sample follows. */}
          {preview.absent_total > 0 && (
            <ImportNotice>
              <strong>
                {preview.absent_total} active vendor{preview.absent_total === 1 ? '' : 's'}
              </strong>{' '}
              {preview.absent_total === 1 ? 'is' : 'are'} not in this file and will be{' '}
              <strong>marked inactive</strong>. Nothing is deleted — an inactive vendor stops matching new
              documents, keeps its history, and can be set back to Active one at a time.
            </ImportNotice>
          )}
          <ImportNames
            heading="Vendors to Be Marked Inactive"
            names={preview.absent.map((v) => v.vendor_name || v.vendor_id)}
            total={preview.absent_total}
            limit={20}
          />
          <ImportNames
            heading="New Vendors"
            names={preview.adds.map((a) => a.values.vendor_name || a.values.vendor_id)}
            limit={40}
          />
          <ImportChanges
            updates={preview.updates}
            keyOf={(u) => u.id}
            title={(u) => u.vendor_name || u.vendor_id}
            changes={(u) => u.changes}
            fieldLabel={(f) => VENDOR_IMPORT_FIELD_LABEL[f]}
          />
        </>
      )}
    </ImportDialog>
  )
}
